import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSelection } from '@/contexts/SelectionContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationContext';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ScoreDistributionBar } from '@/components/optimization-score/ScoreDistributionBar';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { FeedCheckHeader } from '@/components/optimization-score/FeedCheckHeader';
import { readinessEntries } from '@/components/optimization-score/feedCheckReadiness';
import { FeedCheckInsights } from '@/components/optimization-score/FeedCheckInsights';
import { FeedCheckNextStep } from '@/components/optimization-score/FeedCheckNextStep';
import { FeedCheckCostList } from '@/components/optimization-score/FeedCheckCostList';
import { FeedCheckChapters } from '@/components/optimization-score/FeedCheckChapters';
import { ScoreSparkline } from '@/components/optimization-score/ScoreSparkline';
import { ReviewRequestCard } from '@/components/optimization-score/ReviewRequestCard';
import { FeedCheckAnalyzing } from '@/components/optimization-score/FeedCheckAnalyzing';
import { usePriceBenchmarkSummary } from '@/hooks/usePriceBenchmarkSummary';
import { PriceBenchmarkSection } from '@/components/optimization-score/PriceBenchmarkSection';
import { useLinkedAdsAccount } from '@/hooks/useLinkedAdsAccount';
import { useAdsAccounts } from '@/hooks/useAdsPerformance';
import { useAdsConnectFlow } from '@/hooks/useAdsConnectFlow';
import { useAdsFeedSummary } from '@/hooks/useAdsFeedSummary';
import { deriveAdsChapterState, isAdsLinkedState, withLiveDisapproved } from '@/lib/adsFeedSummary';
import { useMcViewerRole } from '@/hooks/useMcViewerRole';
import { AdsChapterRow, AdsChapterRowSkeleton } from '@/components/optimization-score/ads/AdsChapterRow';
import { LiveChapterRow } from '@/components/optimization-score/live/LiveChapterRow';
import { NextStepCta } from '@/components/journey/NextStepCta';
import { useLiveFeedStatus } from '@/hooks/useLiveFeedStatus';
import { useFeedDisapprovals } from '@/hooks/useFeedDisapprovals';
import { useAdsInterest } from '@/hooks/useAdsInterest';
import { WORKBENCH_TO_REVIEW_TARGET, actionTargetFor } from '@/lib/journeyTargets';
import { buildBreakdown } from '@/lib/scoreBreakdown';
import * as Sentry from '@sentry/react';
import { READINESS_RULE_ID, fetchRuleStatusCounts, pickRuleTab, ruleWorkbenchTarget, type WorkbenchReadinessKey } from '@/lib/ruleWorkbenchTarget';
import { Download, Globe, Package, Printer, RefreshCw } from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { captureFlowIntent, clearFlowIntent, getFlowIntent } from '@/lib/flowIntent';
import { formatInteger } from '@/lib/language';
import { timeAgo } from '@/lib/timeAgo';
import { pickBaseline, pickPreBaseline, comparableSnapshots } from '@/lib/baselineSnapshot';
import { isSnapshotStale } from '@/lib/snapshotFreshness';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { trackConversion } from '@/lib/analytics';
import { OptimizationProvider, useOptimization } from '@/contexts/OptimizationContext';
import { useFeedCheckOptimize, resolveOptimizeTarget, type FeedCheckOptimizeSource } from '@/hooks/useFeedCheckOptimize';
import { useJourney } from '@/hooks/useJourney';
import { useEnforcement } from '@/hooks/useEnforcement';
import { useMcAccessStatus } from '@/hooks/useMcAccessStatus';
import { useProductStats } from '@/hooks/useProductStats';
import { useUploadJob } from '@/hooks/useUploadJob';
import { UploadFlowDialogs } from '@/components/upload/UploadFlowDialogs';
import { useRealtimePostgresChanges } from '@/hooks/useRealtimePostgresChanges';
import { useFetchProducts } from '@/hooks/useFetchProducts';
import { useCheckoutSuccess } from '@/hooks/useCheckoutSuccess';
import { OptimizationProgressModal, type OptimizationScoreDelta, type OptimizationSuccessContext } from '@/components/optimization/OptimizationProgressModal';
import { UpgradeModal } from '@/components/enforcement/UpgradeModal';
import { ReoptimizeConfirmDialog } from '@/components/enforcement/ReoptimizeDialogs';
import { QuotaLimitDialog } from '@/components/enforcement/QuotaLimitDialog';
import { needsLargeRunConfirm } from '@/lib/largeRun';
import { scoreTypeForFeed } from '@/lib/scoreTypeForFeed';
import { LargeRunConfirmDialog } from '@/components/optimization-score/LargeRunConfirmDialog';
import { FetchProgressDialog } from '@/components/products/FetchProgressDialog';
import { SafetyHintDialog, useSafetyHint } from '@/components/products/SafetyHintDialog';
import GoogleAccountWizard from '@/components/wizard/GoogleAccountWizard';

/**
 * Task 14: Zaehlung der Produkte, bei denen der naechtliche
 * `scan-merchant-drift`-Sweep eine Aenderung an den Merchant-Originaldaten
 * seit der letzten Optimierung erkannt hat (`products.merchant_drift_detected_at`).
 * Der Partial-Index `idx_products_merchant_drift` auf
 * `(feed_id) WHERE merchant_drift_detected_at IS NOT NULL` macht die
 * `head: true`-Zaehlung billig.
 */
function useDriftCount(feedId: string | null): { count: number; isLoading: boolean } {
  const { data: count = 0, isLoading } = useQuery({
    queryKey: ['merchant-drift-count', feedId],
    enabled: !!feedId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { count, error } = await supabase
        .from('products')
        .select('*', { count: 'exact', head: true })
        .eq('feed_id', feedId as string)
        .not('merchant_drift_detected_at', 'is', null);
      if (error) throw error;
      return count ?? 0;
    },
  });
  return { count, isLoading };
}

const HERO_STYLE = {
  background: 'hsl(var(--primary) / 0.06)',
  border: '1px solid hsl(var(--primary) / 0.12)',
  boxShadow: 'var(--shadow-md)',
};

const STANDARD_STYLE = {
  border: '1px solid hsl(var(--primary) / 0.08)',
  boxShadow: 'var(--shadow-sm)',
};

/*
 * Hier stand `CustomTooltip`. Er gehoerte zum recharts-Verlauf, und der ist mit
 * der Entlastung (Paket 3) einer Sparkline gewichen: vier Datenpunkte, fuenf
 * Gitterlinien und eine Achse von 0 bis 100 fuer eine flache Linie waren der
 * Data-Ink-Verstoss, den das Audit als R4 gemessen hat. Ein Tooltip auf einer
 * Sparkline ist keine Ersatzhandlung, sondern derselbe Fehler kleiner: die
 * beiden Zahlen, die zaehlen (Start und heute), stehen als Text daneben.
 */

/*
 * Hier standen `StatCard` und `AnimatedNumber`. Ihre beiden Aufrufe
 * ("Produkte", "Kritisch") sind mit Task 5 entfallen: die Produktzahl steht
 * in der Bezugsgroesse der Note, die kritische Zahl in der Verlustzeile
 * direkt darunter — beides im Kopf, beides genau einmal.
 */

interface InnerProps {
  /** Onboarding-Assistent. Zustand liegt aussen, damit die Bedingung "null
   *  Merchant Center" nicht am Optimierungs-Kontext haengt (Muster aus dem
   *  aufgeloesten SimpleHome, unveraendert uebernommen). */
  showWizard: boolean;
  setShowWizard: (open: boolean) => void;
}

const FeedHealthDashboardInner: React.FC<InnerProps> = ({ showWizard, setShowWizard }) => {
  const { t, i18n } = useTranslation('optimization');
  const { selectedFeedId, feed, merchantCenter, selectedGoogleAccountId, setSelectedGoogleAccount, refreshSelection } = useSelection();
  const { user } = useAuth();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const optimization = useOptimization();
  const [searchParams] = useSearchParams();
  // Task 9: `?feedcheck=1` ist KEIN Darstellungsschalter mehr. Die Marke sagt
  // nur noch, dass dieser Aufruf aus dem kostenlosen Feed-Check-Trichter kommt,
  // und speist genau eine Kennzahl (`feed_check_report_view`) plus das
  // Verbrauchen des Flow-Markers. Was auf der Seite steht, haengt am
  // gemessenen Zustand des Feeds.
  const isFeedCheck = searchParams.get('feedcheck') === '1';
  const reportViewSent = useRef(false);
  // Nur Laeufe, die auf DIESER Seite gestartet wurden, oeffnen das
  // Fortschritts-Modal. Ein nachts laufender Automatik-Lauf darf niemandem
  // ein Modal aufziehen — genau diese Sorge begruendete frueher das
  // isFeedCheck-Gate, sie bleibt berechtigt und haengt jetzt an der Herkunft.
  const startedHereRef = useRef(false);
  const { toast } = useNotifications();
  const queryClient = useQueryClient();
  const [fillBusy, setFillBusy] = useState(false);

  // Hier lagen `upsellCtaRef`, `primaryCtaInView` und `hasScrolled`: die
  // Mechanik der eingeblendeten Leiste, die den Primaer-CTA nachzog, sobald er
  // aus dem Bild gescrollt war. Sie ist mit der Leiste entfallen -- die
  // Handlungskarte steht seit Paket 3 OBEN (vorher bei 94 Prozent der
  // Scrollhoehe), es gibt also nichts mehr nachzuziehen.

  const { data: snapshots, isLoading, isError, refetch } = useQuery({
    queryKey: ['feed-health-all-snapshots', selectedFeedId],
    queryFn: async () => {
      if (!selectedFeedId) return [];
      const { data, error } = await supabase
        .from('feed_health_snapshots')
        // `*` und nicht eine Spaltenliste: die drei Felder, die Score v3
        // braucht (`product_count`, `attribute_applicable_count`,
        // `avg_base_score`), kommen damit ohne Zutun mit. Eine Liste waere
        // hier die gefaehrlichere Form -- eine vergessene Spalte faellt nicht
        // als Fehler auf, sondern als stille 0 in `buildBreakdown`.
        .select('*')
        .eq('feed_id', selectedFeedId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!selectedFeedId,
    staleTime: 60 * 1000,
  });

  // Task 8: ein fertig gerechneter Befund kam bisher erst beim Neuladen an.
  // `['feed-health-all-snapshots']` wurde einzig aus `FeedCheckAnalyzing`
  // invalidiert, und die Komponente haengt genau an dem Zweig, der mit dem
  // ersten Befund verschwindet -- nach einem Re-Sync bei offenem Report
  // passierte also nichts. `feed_health_snapshots` ist Mitglied der Publication
  // `supabase_realtime`, INSERTs sind damit zustellbar.
  //
  // Ausschliesslich ueber `useRealtimePostgresChanges`: ein direkter
  // `supabase.channel(...)`-Aufruf joint auf einem kalten Seitenaufruf als
  // `anon` und empfaengt schweigend nichts (Mechanik im Kopf des Hooks, dazu
  // der Invarianten-Test). Der Topic traegt die Feed-ID, damit zwei Abos sich
  // nie einen Kanal teilen.
  useRealtimePostgresChanges(
    selectedFeedId ? `feed-health-snapshots-${selectedFeedId}` : null,
    selectedFeedId
      ? {
          event: 'INSERT',
          table: 'feed_health_snapshots',
          filter: `feed_id=eq.${selectedFeedId}`,
        }
      : null,
    () => {
      void queryClient.invalidateQueries({ queryKey: ['feed-health-all-snapshots', selectedFeedId] });
    },
  );

  const { data: validationSummary, isError: validationFailed, error: validationError, refetch: refetchValidation } = useQuery({
    queryKey: ['feed-validation-summary', selectedFeedId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('feed_validation_summary', {
        p_feed_id: selectedFeedId,
      });
      if (error) throw error;
      return data ?? [];
    },
    // Die Regeln laufen fuer JEDEN Feed (Score-Pipeline + feed-check-image-probe
    // schreiben `products.validation_warnings` unabhaengig vom Trichter). Sie
    // nur im Trichter abzufragen hiess: Bestandskunden sehen nie, was Google
    // an ihrem Feed blockiert, obwohl der Befund fuer sie geschrieben wird.
    enabled: !!selectedFeedId,
    staleTime: 60 * 1000,
  });

  // A2-NEU-2: genau einmal je Fehlerobjekt an Sentry (Muster useJourney).
  useEffect(() => {
    if (!validationFailed || !validationError) return;
    const e = validationError as { code?: unknown; message?: unknown };
    Sentry.captureException(validationError, {
      tags: { feature: 'feed-check', action: 'validation-summary' },
      extra: {
        feedId: selectedFeedId,
        code: typeof e.code === 'string' ? e.code : null,
        message: typeof e.message === 'string' ? e.message : null,
      },
    });
    // selectedFeedId bewusst nicht in den Deps: ein Feed-Wechsel ist kein neuer Fehler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationFailed, validationError]);

  // Task 14: einziger Frontend-Konsument von `merchant_drift_detected_at`.
  const { count: driftCount } = useDriftCount(selectedFeedId);

  // WP5 A2O-03: echte Google-Ablehnungen fuer die Baustellen-Liste und das
  // Kapitel "Bei Google" -- `null` heisst nichts zu zeigen, nie "0 abgelehnt".
  const googleRejects = useFeedDisapprovals(selectedFeedId);

  // Report V2 Kapitel 2 (Task 7): serverseitig verdichtete Preis-Benchmarks.
  // `null` heisst "kein Kapitel rendern" (keine Benchmark-Zeilen oder fremder
  // Feed) — die Roh-Zeilen-Query auf `price_benchmarks` samt Client-Rechnung
  // ist damit ersatzlos weg, die RPC liefert das fertige Aggregat.
  const { data: benchmarkSummary, isError: benchmarkFailed, error: benchmarkError, refetch: refetchBenchmark } = usePriceBenchmarkSummary(selectedFeedId);

  // A2-NEU-2: genau einmal je Fehlerobjekt an Sentry (Muster der Validierung).
  useEffect(() => {
    if (!benchmarkFailed || !benchmarkError) return;
    const e = benchmarkError as { code?: unknown; message?: unknown };
    Sentry.captureException(benchmarkError, {
      tags: { feature: 'feed-check', action: 'price-benchmark' },
      extra: {
        feedId: selectedFeedId,
        code: typeof e.code === 'string' ? e.code : null,
        message: typeof e.message === 'string' ? e.message : null,
      },
    });
    // selectedFeedId bewusst nicht in den Deps: ein Feed-Wechsel ist kein neuer Fehler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [benchmarkFailed, benchmarkError]);
  // `priceSectionRef` und `jumpToPrice` sind mit der Preis-Zeile der
  // Top-3-Baustellen entfallen: der Sprung ans Seitenende brauchte ein Ziel
  // weit unten, und das Preis-Kapitel ist jetzt eine Zeile in der
  // Kapitel-Karte, die man aufklappt statt anspringt.

  const latestPre = snapshots?.filter(s => s.snapshot_type === 'pre').slice(-1)[0];
  const latestPost = snapshots?.filter(s => s.snapshot_type === 'post').slice(-1)[0];

  // Der ANGEZEIGTE Befund ist der chronologisch juengste, nicht der
  // Post-Befund mit Vorrang.
  //
  // `latestPost || latestPre` machte das Veraltungs-Abzeichen unloeschbar: ein
  // Feed mit Post-Befund vom 01.06., am 02.08. gesynct, bekommt vom Sync eine
  // neue `pre`-Zeile (`enqueue-score-batches` mit `score_type:
  // 'pre_optimization'`, ausgeloest vom Trigger `trg_feed_check_score`).
  // `latestPost` blieb die Juni-Zeile, also auch `current` — angezeigt wurden
  // der Juni-Score und "geprueft vor 2 Monaten", und weil `feeds.last_synced_at`
  // juenger ist, stand daneben DAUERHAFT "Produkte haben sich seitdem
  // geaendert". Keine Handlung auf dieser Seite loeste das auf: der Auto-Start
  // unten bricht bei vorhandenem `current` ab, und die frisch gerechnete
  // Neuberechnung war damit unsichtbar.
  //
  // Gleichstand geht an den Post-Befund — er ist die spezifischere Aussage,
  // und bei identischem Zeitstempel ist er in der aufsteigend sortierten Liste
  // ohnehin der zuletzt geschriebene.
  //
  // A2-NEU-3 (06.10.2026): damit der juengste Befund auf einem optimierten Feed
  // nicht wieder ein Vorher-Befund wird, bestellen "Neu pruefen", der Auto-Start
  // und der Score nach einem Sync dort `post_optimization`
  // (`lib/scoreTypeForFeed.ts`). Die Anzeige-Regel selbst bleibt unveraendert.
  const current = latestPost && latestPre
    ? (Date.parse(latestPost.created_at) >= Date.parse(latestPre.created_at) ? latestPost : latestPre)
    : (latestPost ?? latestPre);

  // Task 7: die "3 teuersten Baustellen" des Executive-Kopfs.
  // `buildBreakdown(current)` ist dieselbe Rechnung, die der Header intern
  // macht — bewusst doppelt aufgerufen statt Header-intern herausgereicht: die
  // Funktion ist pur und billig, und der Header behaelt seine schmale
  // Snapshot-Schnittstelle. (Steht hier statt beim Benchmark-Hook oben, weil
  // `current` erst an dieser Stelle existiert.)
  // `topFindings` (die "3 teuersten Baustellen") ist mit `TopFindingsCard`
  // entfallen: sie war die erste von DREI Listen, die dieselbe Frage
  // beantworteten. `FeedCheckCostList` beantwortet sie einmal, aus derselben
  // Rechnung `buildBreakdown`.

  // Kapitel 3 (Google Ads, Spec 2026-09-09). Die Seite haelt den Zustand, die
  // Zeile stellt ihn dar -- dieselbe Aufteilung wie beim Preis-Kapitel.
  //
  // `selectedGoogleAccountId` kommt aus der Auswahlkette (Konto -> MC -> Feed)
  // und ist damit dasselbe `google_accounts.id`, das an der aktiven Merchant
  // Center haengt. Die MC-Zeile des Contexts fuehrt die Spalte nicht, und sie
  // dafuer nachzuladen waere eine zweite Quelle fuer dieselbe Aussage.
  const { data: linkedAds, isLoading: linkedAdsLoading } = useLinkedAdsAccount(merchantCenter?.id ?? null);
  const { data: adsAccounts, isLoading: adsAccountsLoading } = useAdsAccounts();
  // Ohne bekanntes Google-Konto gibt es nichts zu vergleichen, und der Zustand
  // ist "nicht verbunden" -- fail-closed statt eines Treffers auf null.
  const hasAnyAdsAccount = !!selectedGoogleAccountId
    && (adsAccounts ?? []).some((a) => a.google_account_id === selectedGoogleAccountId);
  const adsFlow = useAdsConnectFlow({
    source: 'feed_check',
    merchantCenterId: merchantCenter?.id ?? null,
    googleAccountId: selectedGoogleAccountId ?? null,
    feedId: selectedFeedId,
  });
  const { data: adsSummary, isLoading: adsSummaryLoading } = useAdsFeedSummary(
    selectedFeedId,
    linkedAds?.id ?? null,
    { pollUntil: adsFlow.pollUntil },
  );
  // Mitglied eines geteilten Merchant Centers: das Ads-Konto verwaltet der
  // Eigentuemer (A2-NEU-1). Dieselbe Rolle wie in `useJourney`.
  const viewer = useMcViewerRole(merchantCenter?.id ?? null);
  const adsState = deriveAdsChapterState({
    hasAnyAdsAccount,
    linked: linkedAds ?? null,
    parsed: adsSummary ?? null,
    now: new Date(),
    viewerIsMember: viewer.role === 'member',
  });
  // Solange eine der drei Abfragen laeuft, ist "nicht verbunden" keine Aussage,
  // sondern die Abwesenheit einer: alle drei liefern dann `undefined`, und
  // `deriveAdsChapterState` kennt kein Laden. Der Kunde saehe Silhouetten und
  // einen Verbinden-Knopf fuer einen Scope, den er womoeglich laengst erteilt
  // hat. Erreichbar beim Wechsel auf einen Feed unter einem ZWEITEN Merchant
  // Center mit schon gecachtem Befund: die Karte rendert sofort, waehrend
  // `useLinkedAdsAccount` unter neuem Schluessel erst laedt.
  //
  // Der vierte Summand ist das Fenster ZWISCHEN Feed-Auswahl und aufgeloester
  // Merchant Center (Final-Review F6): `useLinkedAdsAccount` ist dann
  // `enabled: false` und meldet deshalb KEIN Laden, `selectedGoogleAccountId`
  // ist null, `hasAnyAdsAccount` false -- die Zeile stuende auf "nicht
  // verbunden" mit einem Verbinden-Knopf, dessen Klick in den Null-MC-Zweig
  // von `afterConnect` laeuft und den Nutzer in die Einstellungen schickt.
  // Dieselbe Ableitung wie in `useJourney` (Spec 3.1): NUR die Verknuepfung
  // entscheidet, ob Schritt 5 erledigt ist -- "Zahlen folgen" ist ein fertiger
  // Schritt mit Wartezustand. Die Karte braucht sie fuer ihren Done-Satz.
  const adsLinked = isAdsLinkedState(adsState);

  const adsLoading = linkedAdsLoading || adsAccountsLoading || adsSummaryLoading
    || viewer.isLoading || (!!selectedFeedId && !merchantCenter);

  /**
   * Die EINE Teaser-Instanz der Seite (Spec 2026-09-16, Abschnitt 3.2).
   *
   * Bis hierher lebte sie in `AdsChapterRow`. Seit die Handlungskarte im
   * Zustand "Alles erledigt" denselben Knopf zeigt, gibt es zwei Orte fuer
   * dieselbe Meldung -- und zwei Hook-Instanzen haetten zwei voneinander
   * unabhaengige `reported`-Zustaende gehabt.
   */
  const adsInterest = useAdsInterest({
    merchantCenterId: merchantCenter?.id ?? null,
    merchantCenterName: merchantCenter?.name ?? null,
    feedName: feed?.display_name ?? null,
    feedLanguage: feed?.content_language ?? null,
    adsCustomerId: linkedAds?.adsCustomerId ?? null,
    summary: adsSummary?.summary ?? null,
    currency: adsSummary?.row.currency ?? null,
  });

  // Kapitel "Bei Google" (Spec 2026-09-16, Abschnitt 6). Liest
  // `supplemental_feeds` plus die ohnehin gemessenen Produktzahlen; kein neuer
  // Backend-Pfad.
  const live = useLiveFeedStatus(selectedFeedId);

  /**
   * Aufklapp-Bitte an genau ein Kapitel (Plan-Entscheidung E4).
   *
   * Zwei Quellen: der Anker `#ads`/`#live` beim Laden der Seite und der
   * Primaerknopf der Handlungskarte fuer Schritt 5. Der `tick` ist noetig,
   * weil dieselbe Bitte zweimal hintereinander kommen kann (zweimal auf
   * denselben Chip geklickt) -- ohne ihn saehe das Kapitel keinen
   * Zustandswechsel und bliebe stumm.
   */
  const [chapterRequest, setChapterRequest] = useState<{ chapter: 'ads' | 'live'; tick: number } | null>(null);
  const requestChapter = useCallback((chapter: 'ads' | 'live') => {
    setChapterRequest((prev) => ({ chapter, tick: (prev?.tick ?? 0) + 1 }));
  }, []);
  /**
   * Eine stehende Bitte wird beim Feedwechsel geraeumt, und das ist kein
   * Aufraeumen, sondern eine Korrektur.
   *
   * `AdsChapterRow` und `AdsChapterRowSkeleton` sind verschiedene
   * Komponententypen an derselben Stelle: kippt `adsLoading` beim Feedwechsel
   * (neuer Query-Key), MONTIERT das Kapitel neu, sein `letzterTick`-Ref ist
   * wieder leer, und der Effect sah dieselbe alte Bitte als neu an -- also
   * Sprung und Fokus auf den Connect-Knopf, ohne dass jemand darum gebeten
   * hat. `LiveChapterRow` ist davon nicht betroffen (kein Skeleton, kein
   * Remount), die Ursache liegt aber in der Bitte und nicht im Kapitel.
   */
  useEffect(() => {
    setChapterRequest(null);
  }, [selectedFeedId]);

  // Review H1 Finding 1: die Kapitelzahl zaehlt die GERENDERTEN Kapitel, nicht
  // die theoretisch moeglichen. Kapitel 2 existiert nur bei vorhandener
  // Benchmark-Summary (haeufigster Fall ohne: Feed ohne GTIN-Matches). Kapitel
  // 3 wird IMMER gerendert und zaehlt deshalb immer mit -- seine Ueberschrift
  // nennt die Nummer, und eine Zeile "Kapitel 3" unter einem "Kapitel 2 von 2"
  // waere die falsche Zusage, nicht der Zaehler. Die Nummer kommt von hier,
  // statt in der Copy zu stehen: ohne Preis-Benchmark ist Ads das zweite
  // Kapitel, nicht das dritte.
  // Seit dem 16.09.2026 kommt das Kapitel "Bei Google" als LETZTES dazu (Spec
  // 2026-09-16, Abschnitt 6): Befund, Preis (nur mit Benchmark), Google Ads,
  // Bei Google. Es rendert immer, wenn ein Feed gewaehlt ist, und zaehlt
  // deshalb immer mit.
  const chapterTotal = 1 + (benchmarkSummary ? 1 : 0) + 2;
  const liveChapterIndex = chapterTotal;
  const adsChapterIndex = chapterTotal - 1;
  // Kapitel 1 ist der Befund (Score-Karte), das Preis-Kapitel steht darunter
  // und existiert nur mit Benchmark. Die Zahl kommt von hier, damit Zeilen-
  // Ueberschrift und `PriceBenchmarkSection` sie aus derselben Rechnung nehmen.
  const priceChapterIndex = 2;

  // Task 9: "Vorher" ist der eingefrorene ERSTBEFUND, nicht der Stand vor dem
  // letzten Lauf (Spec 7). Befunde ohne `scoring_version` stammen aus einer von
  // mindestens vier Formel-Epochen zwischen Maerz und Mai 2026 und taugen
  // weder als Basis noch als Punkt im Verlauf. Die Rechnung dazu liegt in
  // `@/lib/baselineSnapshot`, hier wird sie nur eingehaengt. Geloescht wird
  // nichts, die alten Zeilen bleiben in der Datenbank.
  //
  // WICHTIG fuer den Identitaetsvergleich `baseline !== current` weiter unten:
  // beide leiten aus DERSELBEN `snapshots`-Instanz ab. `pickBaseline` filtert
  // und sortiert nur, die Elemente bleiben dieselben Objekte wie in `latestPre`
  // / `latestPost`. Wer `snapshots` dazwischen durch ein `map` schickt, bricht
  // den Vergleich lautlos, und ein Feed mit genau einem Befund meldete dann
  // stolz plus null gegen sich selbst.
  //
  // Die Zielversion kommt aus dem Befund, der GERADE die Note stellt, nicht aus
  // einer im Frontend gespiegelten Konstante: eine Kopie von SCORING_VERSION
  // muesste bei jedem Edge-Deploy von Hand nachgezogen werden, und wenn das
  // einmal ausbleibt, filtert die Seite gegen eine Version, die niemand mehr
  // schreibt -- es bliebe kein Befund uebrig, ohne dass irgendein Test rot wird.
  // Ohne diesen Parameter liessen beide Funktionen JEDE bekannte Version durch,
  // und der naechste Formelwechsel wuerde dem Nutzer den Sprung als Ergebnis
  // seiner Optimierung melden. Genau dagegen existiert die Spalte.
  const currentVersion = current?.scoring_version ?? null;
  const baseline = useMemo(
    () => pickBaseline(snapshots ?? [], currentVersion),
    [snapshots, currentVersion],
  );
  const trendSource = useMemo(
    () => comparableSnapshots(snapshots ?? [], currentVersion),
    [snapshots, currentVersion],
  );

  // F2: die Basis der "angefasst oder nicht"-Aussage ist eine ANDERE als die
  // der Note im Kopf, und das ist Absicht.
  //
  // `baseline` oben beantwortet "was war der Erstbefund" -- unabhaengig davon,
  // ob er vor oder nach einer Optimierung gemessen wurde. Fuer das Delta der
  // Note ist das richtig.
  //
  // `buildBreakdown` stellt aber eine engere Frage ("hat die Optimierung
  // diese Baustelle angefasst") und beantwortet sie nur, wenn ein POST gegen
  // einen PRE steht (`comparable` in scoreBreakdown.ts). Auf einem
  // Bestandsfeed ist der erste Befund der aktuellen Formel-Version fast immer
  // ein post -- die naechtliche Automatik scort, bevor der Nutzer je einen
  // Feed-Check ausgeloest hat. `pickBaseline` liefert dann genau diesen post,
  // der Vergleich ist unzulaessig, und die Funktion bliebe auf dem Feed
  // dauerhaft stumm, ohne dass irgendetwas rot wird.
  //
  // Die Spec (2026-09-15, 2.3) sagt "dieselbe Wahl wie compareScore"; das
  // Final-Review hat den Fall gefunden, die Abweichung ist ein Ruling des
  // Controllers und steht im Ledger. `compareScore`, `compareCount` und
  // `baselineResetByVersion` bleiben unveraendert an `baseline`.
  //
  // Der Identitaetsvergleich `!== current` gilt hier aus demselben Grund wie
  // oben: `pickPreBaseline` filtert und sortiert nur, die Elemente bleiben
  // dieselben Objekte. EINMAL berechnet, damit Kopf und Kosten-Liste
  // garantiert dieselbe Vorher-Zeile lesen.
  const touchBaseline = useMemo(
    () => pickPreBaseline(snapshots ?? [], currentVersion),
    [snapshots, currentVersion],
  );
  const compareSnapshot = touchBaseline && touchBaseline !== current ? touchBaseline : null;

  // "Noch kein Vergleichswert" ist nur die halbe Wahrheit, wenn durchaus
  // aeltere Befunde EXISTIEREN, aber keiner die Formel-Version des aktuellen
  // traegt (fehlende oder abweichende `scoring_version` — der Filter oben
  // schliesst sie bewusst aus). Direkt nach einem Lauf mit sichtbarem Plus im
  // Erfolgs-Modal liest sich "noch kein Vergleichswert" wie ein Widerspruch;
  // der Kopf nennt dann den wahren Grund: die Basis wurde durch den
  // Formelwechsel neu gesetzt. `current` ist der juengste Befund — gibt es
  // neben ihm weitere Zeilen und trotzdem keine Basis (oder nur ihn selbst als
  // Basis), koennen die anderen nur am Versions-Filter gescheitert sein.
  const baselineResetByVersion =
    (snapshots?.length ?? 0) > 1 && (!baseline || baseline === current);

  // Task 7: traegt der gespeicherte Befund noch? Die Rechnung liegt in
  // `@/lib/snapshotFreshness`, hier wird nur eingesammelt, was sie braucht.
  //
  // `now: new Date()` ist bewusst NICHT reaktiv: die Zahl wird beim Rendern
  // gezogen und altert danach nicht weiter. Das genuegt, weil die Seite bei
  // jedem Feed-Wechsel neu mountet und die Snapshot-Abfrage sie ohnehin neu
  // rendert. Kein Intervall dranbauen -- ein Sekundentakt haette hier nur
  // einen Effekt: der Hinweis erscheint waehrend des Lesens, ohne dass sich
  // an der Datenlage etwas geaendert hat.
  const staleReason = useMemo(
    () => isSnapshotStale({
      snapshotCreatedAt: current?.created_at ?? null,
      lastSyncedAt: feed?.last_synced_at ?? null,
      now: new Date(),
    }),
    [current?.created_at, feed?.last_synced_at],
  );

  useEffect(() => {
    if (isFeedCheck && current && !reportViewSent.current) {
      reportViewSent.current = true;
      trackConversion('feed_check_report_view', { score: Math.round(Number(current.avg_score)) });
      // Funnel-Ziel erreicht: Flow-Marker verbrauchen. Sonst wuerden bei
      // Bestandskunden (Wizard oeffnet nie, dessen onSuccess-Clear laeuft
      // nie) alle spaeteren manuellen Syncs dauerhaft als Feed-Check-Syncs
      // geflaggt und wiederkehrende Report-Mails ausloesen.
      clearFlowIntent();
    }
  }, [isFeedCheck, current]);

  // Gemessener naechster Schritt und die Zahlen dazu. Beide Hooks teilen sich
  // ihre React-Query-Schluessel mit den JourneyChips im Layout-Band, kosten
  // hier also keine zusaetzliche Abfrage.
  const journey = useJourney();
  const { stats, isLoading: statsLoading, statsFeedId, refetchStats, isError: statsError } = useProductStats(selectedFeedId, false, null, null);
  // A2-NEU-2: Zaehlung gescheitert UND keine Zahlen dieses Feeds. Der Fehler geht
  // ueber useJourney an Sentry (dieselbe Query), die Seite meldet nicht doppelt.
  const statsFailed = statsError && statsFeedId !== selectedFeedId;

  // F-B Teil 2 (Zwergehuus-E2E 2026-08-17): der Upload-Knopf der Handlungskarte
  // (unten, `onUploadRun`) oeffnet den Bestaetigungsdialog direkt statt zur
  // Werkbank zu navigieren — derselbe Doppelklick-Umweg, den `WorkbenchUploadAction`
  // fuer die Werkbank schon geloest hatte. Kein `getUploadSelection`-Scope: der
  // Upload von hier ist immer feed-weit, die Einschraenkung macht der Dialog
  // ueber `approved_only` (Muster `WorkbenchUploadAction`).
  const upload = useUploadJob(selectedFeedId, refetchStats);

  // Item A (UX-Nachzuegler 2026-08-17): `upload.isUploading` ist true fuer
  // JEDEN aktiven Upload dieses Feeds (zweiter Tab, naechtlicher Auto-Upload),
  // nicht nur den von dieser Seite ausgeloesten. `uploadBusy` (weiter unten an
  // die Karte gereicht) sperrte den Knopf damit stumm, sobald ein fremder Lauf
  // aktiv war — Muster + Ableitung 1:1 aus `WorkbenchUploadAction.foreignUploadRunning`.
  const foreignUploadRunning = !!upload.activeUploadJob && !upload.uploadStartedHere;

  // Task 10: die angebotene Menge ist keine Konstante mehr, sondern das
  // Minimum aus freier Kapazitaet und unoptimiertem Bestand. Derselbe
  // React-Query-Schluessel wie in `useJourney`, also keine zusaetzliche
  // Abfrage.
  //
  // `remainingCapacity` kommt seit dem Bestandsmass fertig gerechnet vom Server
  // (`check_can_optimize.remaining_capacity`). Die frueher hier gebildete
  // Differenz `limit - count` war die Lifetime-Rechnung: sie zaehlte jedes je
  // optimierte Produkt mit, auch die laengst aus dem Feed geloeschten, und
  // schrumpfte damit ein Angebot, fuer das in Wahrheit Platz war.
  const {
    remainingCapacity,
    nextPlan,
    isLoading: quotaLoading,
    billedPlanIsSpecial,
    isMemberOfForeignMc,
    billedOwnerEmail,
  } = useEnforcement();
  // `optimizable` = not_optimized + failed OHNE Serving-gesperrte; der Server
  // filtert seit 26.08. identisch, der Knopf darf nicht mehr versprechen.
  const worstTargetCount = resolveOptimizeTarget({
    remainingQuota: remainingCapacity,
    selectableCount: stats.optimizable,
  });

  // WP5 A2O-12: bei voller Kapazitaet verschwand das Optimieren-Angebot ohne
  // ein Wort. Jetzt nennt die Karte die Menge und den Ausweg. `kind` folgt
  // der WP1-Kaufflaechen-Einordnung (Ruling D4): ein Mitglied einer fremden
  // Merchant Center sieht das Kontingent des Eigentuemers, nicht sein
  // eigenes -- ein Upgrade-Knopf waere dort ein Angebot, das nicht ihm
  // gehoert, deshalb gewinnt `isMemberOfForeignMc` vor allem anderen. Sonst
  // entscheidet, ob es ueberhaupt ein groesseres Band gibt: ohne eines
  // (Sondertarif oder bereits im groessten Band) bleibt nur der Kontaktweg.
  const capacityFull =
    !quotaLoading && remainingCapacity === 0 && stats.optimizable > 0
      ? {
          count: stats.optimizable,
          kind: isMemberOfForeignMc
            ? ('owner' as const)
            : billedPlanIsSpecial || nextPlan === null
              ? ('contact' as const)
              : ('upgrade' as const),
          ownerEmail: isMemberOfForeignMc ? billedOwnerEmail : null,
        }
      : null;

  // G3: one hook instance, shared by all four entry points — each calls
  // `runOptimize` with its own `source` marker but drives the exact same
  // action. `worstTargetCount` geht hinein, damit die Menge auf dem Knopf und
  // die angeforderte Menge EINE Zahl sind.
  const feedCheckOptimize = useFeedCheckOptimize(
    selectedFeedId,
    optimization.handleOptimize,
    worstTargetCount,
  );

  // Der Wrapper ist die einzige Stelle, die einen Lauf auf dieser Seite
  // ausloest — also die einzige Stelle, an der die Herkunft gesetzt wird.
  // Defense-in-Depth gegen einen zweiten parallelen Lauf: die vier Einstiege
  // sind bei laufendem Job zwar disabled (optimizationRunning-Prop), aber ein
  // vergessener Einstieg darf trotzdem keinen ZWEITEN optimization_job ueber
  // dieselben Worst-N-Produkte anlegen — der Server laesst bis zu 3 parallele
  // Jobs zu und useAsyncJob erreicht nur den neuesten (Abbrechen/Modal saehen
  // Job 1 nie wieder).
  const startRun = (source: FeedCheckOptimizeSource) => {
    startedHereRef.current = true;
    return feedCheckOptimize.runOptimize(source);
  };

  // WP5 A2O-09: ab 500 Produkten fragt der Feed-Check nach, statt sofort
  // Kontingent zu belegen und ebenso viele Freigaben in der Werkbank zu
  // erzeugen. Die Herkunft wird gemerkt, damit der bestaetigte Lauf dieselbe
  // Quelle meldet wie ein direkter Start.
  const [pendingLargeRun, setPendingLargeRun] = useState<FeedCheckOptimizeSource | null>(null);

  const runOptimize = (source: FeedCheckOptimizeSource) => {
    if (optimization.isOptimizing) {
      toast({ title: t('feedCheck.cta.blockedRunning') });
      return;
    }
    if (needsLargeRunConfirm(worstTargetCount, quotaLoading ? null : remainingCapacity)) {
      setPendingLargeRun(source);
      return;
    }
    return startRun(source);
  };

  // Widerrufener MC-Zugriff: der persistente Banner behauptet auf jeder Seite
  // "Optimierungen und Uploads sind deaktiviert" — die beiden Feed-Check-CTAs
  // muessen das auch SEIN, sonst widersprechen sich Banner und Knopf im selben
  // Viewport und der Klick endet nur im Guard-Toast von useOptimizationJob.
  const { isAccessRevoked: isMcAccessRevoked } = useMcAccessStatus();

  // Produkte holen. Seit Task 13 ist das hier die einzige Stelle der App, an
  // der ein Sync von Hand ausgeloest werden kann: der Ladeknopf sass im
  // CardGridLayout des aufgeloesten SimpleHome. Waehrend der Assistent offen
  // ist, gehoert die Fortschrittsanzeige seinem DownloadProductsStep, sonst
  // stehen zwei Dialoge fuer denselben Lauf.
  const fetch_ = useFetchProducts(selectedFeedId, refetchStats, optimization.setScoreJobInfo, showWizard, optimization.isOptimizing);
  const safetyHint = useSafetyHint();

  // Solange die speisenden Quellen nicht aufgeloest sind, liefern sie
  // Platzhalter-Nullen: `useProductStats` gibt vor der Aufloesung EMPTY_STATS
  // zurueck und `merchantCenter` ist null, obwohl `selectedFeedId` schon
  // synchron aus dem localStorage steht. `deriveJourney` macht daraus einen
  // scheinbar gemessenen naechsten Schritt, der nach dem Laden kippt — und ein
  // Klick in diesem Fenster startet den falschen Lauf und verbraucht
  // Kontingent. Also erst rechnen, wenn gemessen wurde.
  //
  // Task 10: das Kontingent gehoert seit der Mengen-Aenderung in dieselbe
  // Bedingung. `useEnforcement` liefert vor der Aufloesung die Platzhalter
  // `limit = 200` / `count = 0`, und daraus wuerde exakt die alte feste 200 auf
  // dem Knopf stehen, die nach dem Laden auf den echten Bestand springt --
  // dieselbe Klasse Fehler, gegen die dieses Gate schon existiert. Ein zweites
  // Gate haette einen zweiten Ladezustand gebraucht; der `cta-pending`-Skeleton
  // deckt beide Quellen ab. `isLoading` ist dabei kein Dauerzustand: der Hook
  // seedet `initialData` aus dem localStorage-Plan-Cache und meldet ohne
  // eingeloggten Nutzer gar keinen Ladevorgang.
  //
  // `statsFeedId === selectedFeedId` ist der dritte Konjunkt und NICHT
  // redundant zu `statsLoading`. `useProductStats` setzt `placeholderData:
  // (prev) => prev`; bei einem Feed-Wechsel meldet React Query damit weiter
  // `status === 'success'`, also `isPending === false` — direkt beim
  // Schluesselwechsel und mit den Zahlen des VORGAENGERS (gemessen gegen
  // @tanstack/react-query@5.83.0, festgenagelt in useProductStats.test.ts).
  //
  // Neu gefaehrlich ist das durch zwei Aenderungen dieses Zweigs: die Menge auf
  // dem Knopf kommt jetzt aus `stats.optimizable` (vorher aus dem Snapshot,
  // der kein placeholderData hat), und die Server-Selection waehlt ueber
  // `feed_id` statt ueber eine ID-Liste. Ein Klick in diesem Fenster
  // beschriftet also die Menge des alten Feeds und reiht die des neuen ein; die
  // Kapazitaets-Vorwarnung in `useOptimizationJob` rechnet mit der falschen Zahl
  // und schlaegt nicht an. Unter dem Bestandsmass ist das nicht mehr endgueltig
  // — der belegte Platz kommt zurueck, sobald die Produkte den Feed verlassen —
  // aber das verbrauchte Fair-Use-Volumen laeuft erst nach 30 Tagen wieder frei,
  // und bis dahin steht der falsche Lauf im Fenster.
  //
  // Ein zweites Ladeflag haette das nicht geloest: der Zustand IST kein
  // Ladezustand, sondern ein Zuordnungsfehler. Ohne Auswahl decken sich beide
  // Seiten auf `null`, das Gate blockiert dort also nichts.
  //
  // Dazu wartet das Gate auf die Leiter (`journey.isLoading`): sie rechnet erst
  // mit geladener Rolle und geladenen Ads-Abfragen, und ein Mitglied saehe
  // sonst kurz "Google-Ads-Konto verbinden", das dann auf den Eigentuemer-Satz kippt.
  const nextStepResolved =
    !!merchantCenter && !statsLoading && statsFeedId === selectedFeedId && !quotaLoading
    && !journey.isLoading;

  /**
   * Der Anker aus der Adresse (Spec 2026-09-16, Abschnitt 6): `/#live` ist das
   * Ziel des Live-Chips, `/#ads` das der Schritte 5 und 6.
   *
   * ERST nach `nextStepResolved`, und das ist der Kern: vorher stehen die
   * Kapitel noch gar nicht im Baum (die Seite rendert ihren Ladezustand), ein
   * `scrollIntoView` liefe ins Leere und die Bitte waere verbraucht. Ein Hash
   * ohne passendes Kapitel wird ignoriert -- es gibt keinen Zustand, in dem
   * die Seite auf einen Anker springt, den sie nicht zeigen kann.
   */
  // `useLocation().hash` und NICHT `window.location.hash`: letzteres ist kein
  // reaktiver Wert. Ein Chip-Klick auf `/#live`, waehrend die Startseite schon
  // offen ist, wechselt nur den Hash -- React Router meldet das, das Fenster
  // nicht, und die Bitte bliebe bis zum naechsten Rendern aus anderem Grund
  // liegen.
  // Verbraucht wird der NAVIGATIONSSCHLUESSEL, nicht der Hash.
  //
  // `location.key` ist je Navigationsvorgang neu, der Hash nicht: steht die
  // Seite schon auf `/#live` und der Nutzer klickt denselben Chip erneut,
  // aendert `navigate('/#live')` die Zeichenkette nicht, und ein Guard auf dem
  // Hash haette die zweite Bitte verschluckt -- genau der Fall, fuer den der
  // `tick` an `requestChapter` gebaut ist. Mit dem Schluessel laeuft der Effect
  // erneut, und das Kapitel sieht einen Wechsel.
  const { hash, key: navigationsSchluessel } = routerLocation;
  const hashVerbraucht = useRef<string | null>(null);
  useEffect(() => {
    if (!nextStepResolved) return;
    const ziel = hash === '#live' ? 'live' : hash === '#ads' ? 'ads' : null;
    if (!ziel) return;
    if (hashVerbraucht.current === navigationsSchluessel) return;
    hashVerbraucht.current = navigationsSchluessel;
    requestChapter(ziel);
  }, [hash, navigationsSchluessel, nextStepResolved, requestChapter]);

  // Beide Zweige nennen die GEMESSENE Menge des jeweiligen Schritts, also
  // dieselbe Zahl wie die Leiter und die Verlustzeile im Kopf. Der Freigabe-Zweig
  // hing an `stats.pendingReview`, dem Lebenszeit-Vorrat: die Handlungskarte
  // nannte damit 347, waehrend Verlustzeile und Freigabe-Knopf im selben
  // Viewport 3 nannten -- drei Einstiege in DIESELBE Handlung mit demselben Ziel
  // /workbench und zwei verschiedenen Zahlen.
  //
  // Das ist ausdruecklich KEIN Sonderfall der Automatik. Die beiden Zahlen
  // decken sich nur, solange nie ein `pending_review`-Produkt hochgeladen wurde;
  // danach treiben sie dauerhaft auseinander, weil `approval_status` nach dem
  // Upload nirgends zurueckgesetzt wird. Live betrifft das 10.409 Zeilen auf 5
  // Feeds -- alle fuenf mit AUSGESCHALTETEN Auto-Upload-Haken (0 von 19
  // Profilen hat einen an, gemessen 2026-07-31).
  // Die Handlungskarte liest `actionStep`, nicht `nextStep`: seit der
  // Kipp-Regel (journey.ts, `APPROVE_FIRST_MAX_RATIO`) koennen die beiden
  // auseinanderfallen. `nextStep` bleibt die Leiter und speist die vier Punkte
  // in der Kopfleiste; `actionStep` ist die Handlung, die diese Karte anbietet.
  //
  // `null` heisst "dieser Schritt hat keine eigene Menge": im Optimieren-Zweig
  // traegt `worstTargetCount` die Zahl, und `pendingReviewAwaitingUpload`
  // waere dort die Menge der ALTERNATIVE -- als `actionCount` uebergeben
  // beschriftete sie den falschen Knopf.
  const nextActionCount = journey.actionStep === 'upload'
    ? stats.approvedAwaitingUpload
    : journey.actionStep === 'approve'
      ? stats.pendingReviewAwaitingUpload
      : null;

  // Ein Angebot ueber eine gemessene Null ist kein Angebot: "0 Produkte
  // freigeben" schickt in eine Werkbank, in der nichts zu tun ist, und ist als
  // einziger Primaer-CTA der Seite eine Sackgasse. Steht der Zeiger auf einem
  // Schritt, dessen Menge 0 misst, faellt die Karte auf das Optimieren-Angebot
  // zurueck -- die naechste wahre Handlung.
  //
  // Nach dem Fix an `awaitingApproval` (journey.ts) kann `deriveJourney` diesen
  // Zustand nicht mehr erzeugen: 'approve' setzt `pendingReviewAwaitingUpload
  // > 0` voraus, 'upload' setzt `approvedAwaitingUpload > 0` voraus, und beide
  // Hooks lesen DENSELBEN React-Query-Eintrag ['simple-product-stats', feedId],
  // koennen also nicht divergieren. Der Schutz bleibt trotzdem, weil er die
  // Klasse Fehler abfaengt, nicht den einen Fall: die Karte ist die letzte
  // Stelle, an der eine Null noch sichtbar wuerde.
  // Der Rueckfall ist 'optimize' und NICHT null, und das ist der Unterschied
  // zwischen zwei Nullen, die sich leicht verwechseln lassen: `actionStep ===
  // null` heisst "gemessen ist nichts offen" (die Karte sagt dann "nichts
  // wartet auf dich"), waehrend eine Menge von 0 auf einem gemessenen Schritt
  // heisst "dieser Schritt ist gegenstandslos, der naechste wahre ist das
  // Optimieren". Beides auf null abzubilden liess die Karte "nichts wartet"
  // behaupten, waehrend eine Zeile darunter die Baustellen des Feeds standen.
  //
  // `ads_campaigns` wird hier auf `null` abgebildet, und das ist keine
  // Vorsichtsmassnahme gegen einen unmoeglichen Fall, sondern die richtige
  // Aussage fuer den Fall, dass er je eintritt: Schritt 6 ist ein Ausblick
  // ohne Handlung (Spec 3, D4), und "keine Handlung" ist genau der
  // Done-Zustand der Karte. `deriveLadderStep` gibt ihn heute nie zurueck --
  // ein `as`-Cast haette diese Zusicherung stillschweigend vorausgesetzt,
  // statt sie zu behandeln.
  const ctaStep =
    journey.actionStep === 'ads_campaigns'
      ? null
      : (journey.actionStep === 'approve' || journey.actionStep === 'upload') && nextActionCount === 0
        ? 'optimize'
        : journey.actionStep;

  // Verbot 10: genau EIN Primaer-CTA pro Zustand. Steht die Freigabe an oder
  // fehlt das Konto, ist ein Optimierungs-Angebot der zweite CTA und der
  // falsche — es bietet Arbeit an, die entweder schon getan ist oder gerade gar
  // nicht laufen kann. Gerechnet wird mit `ctaStep`, damit Karte, Kopf-Knopf und
  // eingeblendete Leiste nie verschiedene Handlungen anbieten.
  //
  // Task 10: `worstTargetCount === 0` heisst, dass es nichts anzubieten gibt --
  // entweder ist das Kontingent leer (dafuer ist der blocked-Zustand der
  // JourneyChips mit Ziel /pricing zustaendig) oder der Bestand ist abgearbeitet.
  // Ein Knopf mit der Aufschrift "0 Produkte optimieren" waere in beiden
  // Faellen ein Angebot ueber eine gemessene Null, dieselbe Sackgasse, die
  // `ctaStep` oben fuer Freigabe und Upload abfaengt.
  const offersOptimize =
    nextStepResolved &&
    worstTargetCount > 0 &&
    ctaStep !== 'approve' &&
    ctaStep !== 'upload' &&
    ctaStep !== 'connect';

  // F-A (Zwergehuus 2026-08-17): Optimieren ist in approve/upload-Zuständen
  // nicht der nächste Schritt, aber weiterhin MÖGLICH — vorher waren alle vier
  // Einstiege gleichzeitig weg (bei Grade F mit 4.914 unoptimierten Produkten
  // gab es keinen Weg zur Kernaktion). Sekundär heißt: Baustellen-Knöpfe an der
  // Diagnose plus dezente Zweitzeile in der Handlungskarte. Hero-CTA und
  // Sticky-Leiste bleiben primär-exklusiv (Verbot 10, gradient-gestylte
  // Angebote). `connect` bleibt draußen (ohne funktionierendes Konto kann kein
  // Lauf starten), ebenso worstTargetCount === 0 (Kontingent-Blockade gehört
  // den JourneyChips mit Ziel /pricing).
  const offersOptimizeSecondary =
    nextStepResolved &&
    worstTargetCount > 0 &&
    (ctaStep === 'approve' || ctaStep === 'upload');
  const nextActionTarget = ctaStep ? actionTargetFor(ctaStep, journey.targets) : '/';

  // Zustand A (kein Snapshot) war eine Sackgasse: die Handlungskarte liegt im
  // `current`-Zweig, der FeedSwitcher traegt hier keinen Ladeknopf, und der
  // Leerzustand hatte keine Aktion. Ein Bestandskunde ohne Score hatte damit
  // keinen einzigen Knopf auf der Seite. Welche Handlung ansteht, entscheidet
  // der gemessene Stand der Kette, nicht eine feste Annahme.
  //
  // Beschriftung und Handlung kommen aus EINER Verzweigung. Vorher hingen sie
  // an zwei unabhaengigen Ternaerketten: bei einem nie gesynchten Feed
  // (nextStep 'connect') stand "Produkte laden" auf einem Knopf, der in die
  // Merchant-Center-Einstellungen navigierte — eine Liste, auf der nichts
  // geladen werden kann. Fuer alle anderen Faelle bleibt der Ladeknopf der
  // Ladeknopf: er tut jetzt, was er sagt.
  const canScoreNow = nextStepResolved && stats.total > 0;

  // Riegel gegen zwei Laeufe, die sich zeitlich UEBERLAPPEN (Knopf-Klick
  // waehrend der Auto-Start noch fliegt). Er faellt im `finally` wieder, ist
  // also kein Dauer-Riegel. Der liegt fuer den Auto-Start in
  // `autoScoredFeedsRef` und, seit der Nachbesserung N1, in `runSeenFeedsRef`.
  const scoreStartRef = useRef(false);
  const userId = user?.id;
  const handleStartScoring = useCallback(async (): Promise<boolean> => {
    if (!selectedFeedId || !userId || scoreStartRef.current) return false;
    scoreStartRef.current = true;
    // A2-NEU-3: hat der Feed optimierte Produkte, wird der Nachher-Befund
    // bestellt. Die Anzeige nimmt den juengsten Befund (siehe `current`); ein
    // frischer Vorher-Befund ersetzte dort sonst den Nachher-Befund. Gilt fuer
    // alle drei Ausloeser dieser Funktion (Neu pruefen, Auto-Start, Knopf im
    // Leerzustand). Gehoert die Statistik noch nicht zum gewaehlten Feed, bleibt
    // es bei pre_optimization (`scoreTypeForFeed(null)`).
    const scoreType = scoreTypeForFeed(
      !statsLoading && statsFeedId === selectedFeedId ? stats.optimized : null,
    );
    try {
      const { error } = await supabase.functions.invoke('enqueue-score-batches', {
        body: { feed_id: selectedFeedId, score_type: scoreType, user_id: userId },
      });
      if (error) throw error;
      // Damit `FeedCheckAnalyzing` den frisch eingereihten Job sofort sieht
      // (useAsyncJob-Schluesselpraefix, siehe dort).
      await queryClient.invalidateQueries({ queryKey: ['feed-check-analyzing-score-job'] });
      return true;
    } catch {
      toast({ title: t('feedCheck.analyzing.emptyAction.error'), variant: 'destructive' });
      return false;
    } finally {
      scoreStartRef.current = false;
    }
  }, [selectedFeedId, userId, queryClient, toast, t, stats.optimized, statsLoading, statsFeedId]);

  // NA1 Task 1b (Ruling N3): laeuft gerade eine Optimierung, bestellt die Seite
  // keinen eigenen Befund. Der Lauf bestellt am Ende selbst den Nachher-Befund
  // (finalizeJob, mit source_optimization_job_id). Ein von hier bestellter
  // Nachher-Job ohne Quelle, der in dem Moment noch laeuft, unterdrueckte ihn
  // (enqueue-score-batches, 'live-job'), und die Produkte des Laufs bekaemen
  // keinen Nachher-Score. Vor A2-NEU-3 konnte das nicht passieren, die Seite
  // bestellte nur Vorher-Befunde.
  const scoreBlockedByRun = useCallback((): boolean => {
    if (!optimization.isOptimizing) return false;
    toast({ title: t('feedCheck.stale.recheckDuringRun') });
    return true;
  }, [optimization.isOptimizing, toast, t]);

  // WP5 A2O-22: "Neu pruefen" am Veraltet-Badge. Der Befund bleibt stehen,
  // bis der neue Snapshot per Realtime eintrifft; der Satz sagt das.
  const [recheckBusy, setRecheckBusy] = useState(false);
  const handleRecheck = useCallback(async () => {
    if (scoreBlockedByRun()) return;
    // Final-Review WP5: die beiden Riegel von `handleStartScoring` gaben
    // `false` ohne Toast zurueck, der Klick blieb dann stumm (A2O-22).
    if (scoreStartRef.current) {
      toast({ title: t('feedCheck.stale.recheckAlreadyRunning') });
      return;
    }
    if (!selectedFeedId || !userId) {
      toast({ title: t('feedCheck.analyzing.emptyAction.error'), variant: 'destructive' });
      return;
    }
    setRecheckBusy(true);
    const ok = await handleStartScoring();
    setRecheckBusy(false);
    if (ok) toast({ title: t('feedCheck.stale.recheckStarted') });
  }, [scoreBlockedByRun, handleStartScoring, selectedFeedId, userId, toast, t]);

  /**
   * Task 8: die Seite startet die Berechnung selbst, statt einen Leerzustand
   * mit Knopf fuer etwas anzubieten, das sie ohne Rueckfrage aufloesen kann.
   * 232 von 290 Feeds haben ueberhaupt keinen Befund -- die Seite hat bisher
   * nie einen angefordert, sie hat nur gelesen.
   *
   * Dedupliziert wird SERVERSEITIG (Live-Job-Guard in `enqueue-score-batches`);
   * der Ref hier verhindert nur, dass ein Rerender einen zweiten Aufruf
   * ausloest. Er haelt die schon angestossenen Feed-IDs als MENGE statt nur der
   * letzten: mit einem einzelnen Slot feuerte ein Hin- und Zurueckwechseln
   * (A -> B -> A) den Auto-Start fuer A ein zweites Mal, obwohl der Kommentar
   * daneben das Gegenteil behauptete. Der Server haette den zweiten Lauf
   * ohnehin verworfen — nur soll sich der Client nicht auf einen fremden Guard
   * verlassen fuer eine Zusage, die er selbst gibt. Die Menge waechst pro
   * Sitzung um einen String je besuchtem Feed.
   *
   * Bewusst NICHT an `staleReason` gehaengt: eine automatische Neuberechnung
   * bei jedem Oeffnen eines alten Feeds wuerde Last erzeugen, die an keiner
   * Nutzerhandlung haengt. Veraltete Befunde werden markiert (Task 7); die
   * Neuberechnung startet dort ueber den sichtbaren Knopf.
   *
   * `runSeenFeedsRef` haelt daneben die Feeds, auf denen die Seite einen
   * Optimierungslauf gesehen hat. Dort startet der Auto-Start fuer den Rest des
   * Seitenbesuchs nicht mehr (Begruendung im Effekt).
   */
  const autoScoredFeedsRef = useRef<Set<string>>(new Set());
  const runSeenFeedsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!selectedFeedId) return;
    // NA1 Task 1b, Nachbesserung N1: waehrend eines Laufs startet nichts, und
    // ein Feed, auf dem die Seite einen Lauf gesehen hat, startet auch danach
    // nicht mehr von selbst. Endet der Lauf erfolgreich, bestellt finalizeJob
    // den Befund mit der Lauf-Quelle (source_optimization_job_id); ein eigener
    // Auto-Start kaeme im Fenster zwischen Laufende und diesem Aufruf und
    // wuerde ihn unterdruecken (enqueue-score-batches, 'live-job', Folgebefund
    // NA1-F2). Scheitert der Lauf oder wird er abgebrochen, bleibt der Knopf
    // im Leerzustand, der Nutzer startet von Hand. Die Markierung steht vor
    // den uebrigen Riegeln, damit sie auch greift, solange die Seite laedt,
    // schon einen Befund hat oder noch nicht scoren kann.
    if (optimization.isOptimizing) {
      runSeenFeedsRef.current.add(selectedFeedId);
      return;
    }
    if (isLoading) return;
    if (current) return;
    if (!canScoreNow) return;
    if (runSeenFeedsRef.current.has(selectedFeedId)) return;
    if (autoScoredFeedsRef.current.has(selectedFeedId)) return;
    autoScoredFeedsRef.current.add(selectedFeedId);
    void handleStartScoring();
  }, [selectedFeedId, isLoading, current, canScoreNow, optimization.isOptimizing, handleStartScoring]);

  // Konvergenz-5: `connect` feuert in journey.ts AUCH fuer einen verbundenen,
  // aber nie gesynchten Feed (lastSyncedAt null && total 0) — und praktisch
  // NUR fuer den: das Gate `nextStepResolved` verlangt ein geladenes Merchant
  // Center, im echten Kein-MC-Fall bleibt emptyAction also ohnehin undefined
  // (der Wizard liegt darueber). "Merchant Center verbinden" ->
  // /settings?tab=merchants war fuer diese Nutzer eine falsche Aussage in
  // eine Sackgasse (dort laedt nichts Produkte), waehrend die wahre Handlung
  // "Produkte laden" existiert. Das Connect-Pairing bleibt deshalb nur fuer
  // den einzigen hier erreichbaren Fall, in dem es stimmt: die Verbindung ist
  // wirklich kaputt (Zugriff widerrufen — dieselbe Ableitung wie `syncFailed`
  // in useJourney).
  const connectIsBroken = merchantCenter?.access_status === 'permission_denied';

  // Die Verzweigung steht als eigener Wert da, nicht nur als Ternaerkette in
  // `emptyAction`: seit Task 11 haengt auch der Sync-Knopf im Seitenkopf davon
  // ab (siehe `showHeaderSync`). Zwei Stellen, die dieselbe Bedingung getrennt
  // nachbauen, laufen frueher oder spaeter auseinander.
  const emptyActionKind: 'score' | 'connect' | 'load' | null = !nextStepResolved
    ? null
    : canScoreNow
      ? 'score'
      : journey.nextStep === 'connect' && connectIsBroken
        ? 'connect'
        : fetch_.fetching
          // Waehrend der Sync laeuft, verschwindet der Ladeknopf: ein zweiter
          // Klick wuerde den laufenden Job serverseitig abbrechen (Paid-Pfad in
          // enqueue-product-sync), und den Fortschritt zeigt der Dialog.
          ? null
          : 'load';

  const emptyAction: { label: string; onAction: () => void } | undefined =
    emptyActionKind === 'score'
      ? { label: t('feedCheck.analyzing.emptyAction.score'), onAction: () => { if (!scoreBlockedByRun()) void handleStartScoring(); } }
      : emptyActionKind === 'connect'
        ? {
            // Ruling D6 (WP5 Task 12): `journey.targets.connect` zeigt bei
            // vorhandenem Merchant Center auf `/` (siehe useJourney.ts). Hier
            // ist der Zugriff widerrufen, das echte Ziel ist "Neu verbinden"
            // im Accounts-Tab der Einstellungen.
            label: t('feedCheck.cta.next.connect'),
            onAction: () => navigate('/settings?tab=accounts'),
          }
        : emptyActionKind === 'load'
          ? { label: t('feedCheck.analyzing.emptyAction.load'), onAction: fetch_.handleFetchProducts }
          : undefined;

  // Hier lagen `hasCurrentSnapshot` und die zwei Effekte der eingeblendeten
  // Leiste: ein IntersectionObserver auf der Handlungskarte und ein
  // Scroll-Listener mit `capture: true` (der Scroll-Container ist `main`, nicht
  // `window`). Alle drei sind mit der Leiste entfallen -- `hasCurrentSnapshot`
  // hatte danach keinen Leser mehr, der Auto-Start prueft `current` selbst.

  // WP5 A2O-02: vom Befund zu genau den betroffenen Produkten. Die Zaehlung je
  // Reiter bestimmt, wo sie liegen; scheitert sie, fuehrt der Klick trotzdem
  // mit Regel in die Werkbank (Reiter dann nach Praeferenz) statt nirgendwohin.
  // Waehrend der Zaehlung ist der Link gesperrt: der Ref faengt den Doppelklick
  // im selben Tick (setState wirkt nicht synchron), der State sperrt den Knopf.
  //
  // Fix-Runde 1 (Review-Finding 1): die Zaehlung dauert kalt mehrere Sekunden
  // (drei parallele Kopfzaehlungen mit jsonb-contains). Klickt der Nutzer
  // waehrenddessen in die Sidebar oder wechselt den Feed, darf `navigate`
  // danach nicht mehr feuern -- sonst reisst es ihn Sekunden spaeter aus einer
  // Seite, die er laengst verlassen hat. `mountedRef` faengt das Entladen.
  // Fuer den Feedwechsel reicht der geschlossene `selectedFeedId`-Wert der
  // Closure NICHT: er ist innerhalb eines Aufrufs immer identisch mit sich
  // selbst. `feedIdRef` haelt stattdessen den JEWEILS AKTUELLEN Feed nach,
  // unabhaengig davon, welche Instanz von `openRuleInWorkbench` gerade laeuft.
  const [ruleBusyKey, setRuleBusyKey] = useState<WorkbenchReadinessKey | null>(null);
  const ruleBusyRef = useRef(false);
  const mountedRef = useRef(true);
  const feedIdRef = useRef(selectedFeedId);
  feedIdRef.current = selectedFeedId;
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const openRuleInWorkbench = useCallback(async (key: WorkbenchReadinessKey) => {
    if (!selectedFeedId || ruleBusyRef.current) return;
    const feedAtClick = selectedFeedId;
    ruleBusyRef.current = true;
    setRuleBusyKey(key);
    const ruleId = READINESS_RULE_ID[key];
    let target = ruleWorkbenchTarget(ruleId, null);
    try {
      const counts = await fetchRuleStatusCounts(feedAtClick, ruleId);
      target = ruleWorkbenchTarget(ruleId, pickRuleTab(counts));
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: 'feed-check', action: 'rule-workbench-link' } });
    } finally {
      ruleBusyRef.current = false;
      if (mountedRef.current) setRuleBusyKey(null);
    }
    if (mountedRef.current && feedIdRef.current === feedAtClick) navigate(target);
  }, [selectedFeedId, navigate]);

  // Deterministic Grundpreis-Fill (Welle 2, fill-unit-pricing) — triggered from the
  // unitPricing readiness entry's fill button only (FeedCheckHeader gates the button).
  const handleFillUnitPricing = async () => {
    if (!selectedFeedId || fillBusy) return;
    setFillBusy(true);
    try {
      const { error } = await supabase.functions.invoke('fill-unit-pricing', {
        body: { feed_id: selectedFeedId },
      });
      if (error) throw error;
      await queryClient.invalidateQueries({ queryKey: ['feed-validation-summary', selectedFeedId] });
    } catch {
      toast({ title: t('feedCheck.readiness.unitPricing.fillError'), variant: 'destructive' });
    } finally {
      setFillBusy(false);
    }
  };

  // Zwei Filter liegen hier uebereinander, und ihre Reihenfolge ist nicht
  // beliebig.
  //
  // (1) `trendSource` haelt nur Befunde ab dem Erstbefund und nur solche mit
  //     bekannter Formel (Task 9). Ein Diagramm, das Punkte aus verschiedenen
  //     Formeln nebeneinander zeichnet, behauptet eine Vergleichbarkeit, die
  //     es nicht gibt (Spec 7): allein durch die v2-Rechnung fiel ein
  //     Rohprodukt von 70,00 auf 24,75.
  // (2) Erst danach der Typ. Pre- und Post-Befunde ungefiltert zu mischen
  //     erzeugt bei einem Feed, der zwischen Sync und Optimierung pendelt, ein
  //     Zackenmuster statt eines Trends — deshalb nur der Typ, der gerade den
  //     aktuellen Score stellt (siehe `current` oben).
  //
  // Ehrlich gesagt: heute liefern beide Reihenfolgen dieselbe Menge. Der
  // Typ-Filter ist ein reines Element-Praedikat, und der Datums-Schnitt in
  // `comparableSnapshots` wirft aus einer bereits typgefilterten Liste nichts
  // mehr weg (die Basis waere dann selbst der aelteste Punkt darin). Die
  // Reihenfolge steht trotzdem fest, aus zwei Gruenden:
  //
  // (a) `trendSource` ist DIESELBE Rechnung, aus der oben `baseline` kommt.
  //     Kopf und Diagramm koennen so nicht auseinanderlaufen.
  // (b) Der Erstbefund ist eine Eigenschaft des FEEDS, nicht eines
  //     Befund-Typs. Zuerst auf 'post' zu filtern liesse `pickBaseline` den
  //     aeltesten POST-Befund waehlen. Das faellt heute nicht auf und wird
  //     falsch, sobald sich der Typ-Filter aendert -- lautlos, weil das
  //     Diagramm dann einfach spaeter beginnt und nichts fehlt aussieht.
  //
  // Der Typ kommt aus `current`, nicht aus `latestPost ? 'post' : 'pre'`. Beide
  // waren dasselbe, solange `current` dem Post-Befund Vorrang gab; seit
  // `current` der chronologisch juengste Befund ist, faellt das auseinander —
  // ein Feed mit altem Post-Befund und frischem Pre-Befund haette die Note aus
  // der Pre-Reihe angezeigt und den Verlauf ueber die Post-Reihe gezeichnet.
  // Die Sparkline bekommt ISO-Datum und Zahl und formatiert selbst -- vorher
  // wurde hier auf "TT.MM" vorformatiert, weil die recharts-Achse einen
  // fertigen Tick brauchte. Die Sparkline hat keine Achse; ihre beiden Zahlen
  // (Start und heute) nennen das Jahr mit, und ein Verlauf ueber Monate ohne
  // Jahr ist eine Angabe, die man raten muss.
  const trendPoints = trendSource
    .filter((s) => s.snapshot_type === (current?.snapshot_type ?? 'pre'))
    .map((s) => ({ date: s.created_at, score: Number(s.avg_score) }));

  // Q6: identical body for both views.
  //
  // Task 6: hier steht nur noch, was zur Handlung gehoert. Verteilung und
  // Verlauf liegen seit Task 7 als `kapitel1Rest` direkt sichtbar weiter oben,
  // der Preis ist Kapitel 2 (`PriceBenchmarkSection`).
  // Druckkopf des Reports (Paket 3): am Bildschirm sagt die Seitenkopfzeile
  // "<Feed> · <N> Produkte geprueft <Zeit>" dasselbe, und zwar oben statt
  // unten. Gedruckt fehlt diese Kopfzeile, also traegt der Report seinen
  // eigenen Kopf mit Feedname und Sprach-Badge.
  const printHeader = current ? (
        <>
          {/* Feed-Info. Sie traegt nur noch, was nirgends sonst auf der Seite
              steht: welcher Feed gemeint ist, in welcher Sprache und fuer
              welches Land. Donut und Lift-Balken fielen mit Task 5 (Score und
              Delta stehen im Kopf).

              Mit dieser Welle fallen drei weitere Zeilen, alle aus derselben
              Snapshot-Spalte wie eine Zeile eine Bildschirmhoehe weiter oben
              beziehungsweise in der Verteilungs-Karte (seit Task 7 direkt
              sichtbar als Kapitel-1-Rest):
                - Pruefdatum -> `checked-at` im Kopf (dort mit Veraltungs-Hinweis),
                - Produktzahl -> `score-basis` im Kopf (dort mit Tausendertrennung
                  via `formatInteger`; hier stand sie roh) und zusaetzlich in der
                  Kopfzeile der Score-Verteilung (`distribution.products`),
                - "gesund"-Anteil -> derselbe Quotient `good_count /
                  product_count` steht in der Score-Verteilung als
                  `distribution.good … (N%)`. */}
          <Card style={STANDARD_STYLE} className="hidden print:block">
            <CardContent className="pt-6 pb-6">
              <div className="flex flex-col gap-3">
                <div>
                  <h2 className="text-lg font-heading font-bold">{t('feedHealth')}</h2>
                  {feed?.display_name && (
                    <p className="text-sm text-muted-foreground">{feed.display_name}</p>
                  )}
                </div>
                {feed?.content_language && feed?.target_country && (
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full w-fit"
                    style={{ background: 'hsl(var(--primary) / 0.08)', color: 'hsl(var(--primary))' }}
                  >
                    <Globe className="h-3 w-3" />
                    {feed.content_language}-{feed.target_country}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </>
  ) : null;

  // Zeile 3 der Seite: Verteilung und Verlauf, zwei kleine Karten
  // nebeneinander (Spec 5.3). Die recharts-Karte "Score-Trend" ist einer
  // Sparkline gewichen und die Karte "Score-Aufschluesselung" der
  // Verteilungs-Karte -- letztere hiess bis Paket 3 so und zeigte die
  // Verteilung, was zwei Namen fuer eine Sache waren.
  //
  // `trendSource` bleibt die Quelle: dieselbe Rechnung, aus der auch die
  // Vergleichsbasis der Score-Karte kommt (siehe `baseline` oben), damit Kopf
  // und Verlauf nicht auseinanderlaufen koennen.
  const kapitel1Rest = current ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <Card style={STANDARD_STYLE} data-testid="score-distribution">
              <CardContent className="pt-6 pb-6">
                <ScoreDistributionBar
                  criticalCount={current.critical_count}
                  warningCount={current.warning_count}
                  goodCount={current.good_count}
                  totalCount={current.product_count}
                  variant="detailed"
                />
              </CardContent>
            </Card>

            <ScoreSparkline points={trendPoints} />
          </div>
  ) : null;

  /**
   * Task 11: der einzige manuelle Sync-Knopf der App.
   *
   * Er sass bis hierher im `ContextSelector`, und der ist seit diesem Task
   * global in der Kopfzeile — dort traegt er bewusst keinen Ladeknopf, sonst
   * stuende auf jeder Seite ein Angebot fuer einen Lauf, dessen Fortschritt
   * nur diese Seite anzeigen kann (FetchProgressDialog, SafetyHintDialog).
   * Also bleibt er hier, im Seitenkopf, und ist in genau denselben Zustaenden
   * sichtbar wie vorher: sobald ein Feed gewaehlt ist.
   *
   * Die eine Ausnahme ist neu und beabsichtigt: bietet der Leerzustand darunter
   * selbst "Produkte laden" an (`emptyActionKind === 'load'`), verschwindet der
   * Kopf-Knopf. Zwei Ladeknoepfe uebereinander sind kein Angebot, sondern eine
   * Frage. Der Leerzustand rendert nur in genau diesem Zweig — Feed gewaehlt,
   * fertig geladen, kein Fehler, kein Befund (siehe Verzweigung unten).
   */
  const emptyStateVisible = !!selectedFeedId && !isLoading && !isError && !current;
  const showHeaderSync = !!selectedFeedId && !(emptyStateVisible && emptyActionKind === 'load');

  return (
    <div className="space-y-6">
      {/* Ein Titel fuer beide Einstiege. Er deckt sich mit Navigation und
          Brotkrume; die Trichter-Ueberschrift war die letzte Stelle, an der
          die URL noch die Darstellung bestimmte. */}
      {/* Die Unterzeile nennt den Feed, die gepruefte Menge und das
          Pruefdatum (Spec 5.3). Alle drei standen bis Paket 3 IN der
          Score-Karte, wo sie ihre zwoelf Woerter Meta-Zeile allein fuellten;
          hier oben beantworten sie die Frage "worueber redet diese Seite",
          bevor die erste Zahl kommt. Ohne Befund bleibt der allgemeine
          Untertitel: eine erfundene Menge waere schlimmer als keine. */}
      <PageHeader
        title={t('pageHeader.scoreTitle')}
        subtitle={current && feed?.display_name
          ? t('pageHeader.scoreSubtitleFeed', {
              feed: feed.display_name,
              count: Math.round(Number(current.product_count ?? 0)),
              formatted: formatInteger(Math.round(Number(current.product_count ?? 0)), i18n?.language),
              ago: timeAgo(current.created_at, t),
            })
          : t('pageHeader.scoreSubtitle')}
        actions={showHeaderSync || current ? (
          <div className="flex items-center gap-2">
          {/* Ein und derselbe String traegt sichtbares Label, aria-label und
              title (uebernommen aus dem ContextSelector, WCAG 2.5.3 Label in
              Name). Der Schluessel bleibt im `dashboard`-Namensraum, damit es
              fuer dieselbe Handlung weiterhin genau einen Wortlaut gibt. */}
          {showHeaderSync && (
          <Button
            onClick={fetch_.handleFetchProducts}
            disabled={fetch_.fetching}
            aria-label={t('dashboard:context.syncProducts')}
            aria-busy={fetch_.fetching}
            title={t('dashboard:context.syncProducts')}
            variant="outline"
            size="sm"
            className="print:hidden"
          >
            {fetch_.fetching ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            <span className="ml-1.5 hidden sm:inline">{t('dashboard:context.syncProducts')}</span>
          </Button>
          )}

          {/* D9: der EINZIGE `window.print()`-Aufrufer der App sass in
              `FeedCheckUpsellCta`. Mit dem Loeschen der Karte waere der
              Druck-Report unerreichbar geworden -- und zwar genau der Report,
              in den dieses Paket die Notenskala, den Potenzial-Strip, die
              Titel-Diffs und den Feed-Gesundheitsbericht verschiebt. Er sitzt
              deshalb hier, neben dem Sync-Knopf: gedruckt wird die Seite,
              nicht eine Karte. `outline`, damit die Regel "genau ein
              gefuellter Knopf" gewahrt bleibt. */}
          {current && (
            <Button
              onClick={() => window.print()}
              variant="outline"
              size="sm"
              className="print:hidden"
              title={t('feedCheck.cta.print')}
              aria-label={t('feedCheck.cta.print')}
            >
              <Printer className="h-4 w-4" />
              <span className="ml-1.5 hidden sm:inline">{t('feedCheck.cta.print')}</span>
            </Button>
          )}
          </div>
        ) : undefined}
      />

      {/* Die Score-Karte und die Handlungskarte lagen bis Paket 3 an zwei
          Enden der Seite: der Kopf hier oben, die Handlung bei 94 Prozent der
          Scrollhoehe. Beide stehen jetzt als Zeile 1 im `current`-Zweig weiter
          unten -- eine Bedingung statt zweier, die dasselbe pruefen. */}

      {!selectedFeedId ? (
        <EmptyState
          icon={Package}
          title={t('selectFeed')}
          description={t('selectFeedDesc')}
        />
      ) : isLoading ? (
        <div className="space-y-4 print:hidden">
          {/* Skelett spiegelt `dashboardBody`, und das ist seit Task 6 nur noch
              die Feed-Info-Karte. Donut und Kennzahl-Kacheln fielen mit Task 5.
              Mit den beiden entdoppelten Zeilen (Produktzahl/gesund-Anteil,
              Pruefdatum) sind auch ihre Platzhalter weg: die Karte hat jetzt
              genau drei Elemente. */}
          <Card style={HERO_STYLE}>
            <CardContent className="py-8">
              <div className="flex flex-col gap-3">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-6 w-20 rounded-full" />
              </div>
            </CardContent>
          </Card>
        </div>
      ) : isError ? (
        <ErrorState onRetry={() => refetch()} />
      ) : !current ? (
        // `FeedCheckAnalyzing` faellt selbst auf den Leerzustand zurueck, wenn
        // fuer diesen Feed nie ein Score-Job lief — der frühere zweite Zweig
        // hier war eine Verdopplung derselben Entscheidung. Die Aktion in
        // diesem Leerzustand kommt von hier, weil nur die Seite den Stand der
        // Kette kennt (Produkte vorhanden? Konto verbunden?).
        <FeedCheckAnalyzing
          feedId={selectedFeedId}
          emptyActionLabel={emptyAction?.label}
          onEmptyAction={emptyAction?.onAction}
          emptyKind={emptyActionKind}
        />
      ) : (
        <>
          {/* Druckkopf: am Bildschirm sagt die Seitenkopfzeile dasselbe. */}
          {printHeader}

          {/* Zeile 1: Score-Karte und Handlungskarte nebeneinander. Unter `lg`
              stapeln sie, die Score-Karte zuerst -- die Diagnose vor der
              Handlung, so wie die Seite auch gelesen wird. */}
          <div className="grid gap-4 lg:grid-cols-2 print:block">
            <FeedCheckHeader
              snapshot={current}
              hasPost={!!latestPost}
              // Delta nur aus zwei echten Messungen, und die linke Seite ist
              // der Erstbefund (Spec 7), nicht der letzte Vorher-Befund.
              // `baseline !== current` haelt den Fall ab, dass ein Feed mit
              // genau einem Befund sich selbst als Vergleichswert bekommt und
              // plus null meldet -- eine Aussage ueber Fortschritt, wo gar
              // keine Messung zum Vergleichen existiert.
              compareScore={baseline && baseline !== current ? Number(baseline.avg_score) : null}
              compareCount={baseline && baseline !== current ? Number(baseline.product_count) : null}
              compareSnapshot={compareSnapshot}
              baselineReset={baselineResetByVersion}
              feedTotal={stats.total}
              checkedAt={current?.created_at ?? null}
              staleReason={staleReason}
              chapterTotal={chapterTotal >= 2 ? (chapterTotal as 2 | 3 | 4) : undefined}
              onRecheck={canScoreNow ? handleRecheck : undefined}
              recheckBusy={recheckBusy}
            >
              {/* Die Titel-Diffs sind Werkbank-Inhalt (Spec 5.3) und stehen am
                  Bildschirm nicht mehr auf dieser Seite. Im Druck-Report
                  bleiben sie: dort sind sie der Beleg fuer das, was die Note
                  behauptet, und die Werkbank liegt nicht bei. */}
              <div className="hidden print:block" data-testid="feedcheck-insights">
                <FeedCheckInsights
                  feedId={selectedFeedId}
                  mode={latestPost ? 'real' : 'examples'}
                  weakCount={Number(current.critical_count) + Number(current.warning_count)}
                  optimizeCap={worstTargetCount}
                />
              </div>
            </FeedCheckHeader>

            {nextStepResolved ? (
              <FeedCheckNextStep
                step={ctaStep}
                // Die Kipp-Regel macht Optimieren primaer und die Freigabe zur
                // Alternative (journey.ts, `APPROVE_FIRST_MAX_RATIO`). Nur in
                // diesem Fall ist `actionSecondaryStep` gesetzt.
                secondaryStep={journey.actionSecondaryStep === 'approve' ? 'approve' : null}
                secondaryCount={stats.pendingReviewAwaitingUpload}
                actionCount={nextActionCount}
                optimizeCount={worstTargetCount}
                // Schritt 5 navigiert NICHT (Spec 3.2): der Knopf klappt
                // Kapitel 3 auf, scrollt dorthin und setzt den Fokus auf den
                // Connect-Knopf. Der Ablauf selbst bleibt dort, wo er lebt --
                // eine Kopie von `useAdsConnectFlow` in dieser Karte waere ein
                // zweiter Connect-Weg samt Popup-Sperre und Rueckweg.
                onAction={ctaStep === 'ads_connect'
                  ? () => requestChapter('ads')
                  : () => navigate(nextActionTarget)}
                adsLinked={adsLinked}
                adsOwnerManaged={adsState === 'owner_managed'}
                adsAccountConnected={hasAnyAdsAccount}
                adsChapterIndex={adsChapterIndex}
                /* Spec 3.2/D4 bindet den Teaser an "Zeiger `null` NACH Schritt 5".
                   Ohne verknuepftes Ads-Konto ist der `Done`-Zustand ueber
                   `isEmpty` erreichbar (nichts zu optimieren, kein Ads-Konto),
                   und dort hat eine Bitte um Interesse an Segment-Zahlen keinen
                   Gegenstand -- es gibt noch kein Konto, das welche liefert. */
                onInterest={adsLinked ? adsInterest.report : undefined}
                interestSent={adsInterest.reported}
                interestBusy={adsInterest.isReporting}
                // WP5 A2O-11: derselbe Weg wie Schritt 5 -- Kapitel 3
                // aufklappen und dorthin scrollen, kein eigener Connect-Weg.
                onOpenAds={adsLinked ? () => requestChapter('ads') : undefined}
                capacityFull={capacityFull}
                onRun={() => runOptimize('cta')}
                onSecondary={() => navigate(actionTargetFor('approve', journey.targets))}
                onQuota={() => navigate('/pricing')}
                // F-A: Optimieren bleibt in approve/upload-Zustaenden sichtbar,
                // nur eben als Alternative neben dem Primaer-Knopf.
                offersOptimizeSecondary={offersOptimizeSecondary}
                // Eine gemessene Null darf nicht als Angebot erscheinen. Zwei
                // Wege fuehren dorthin und brauchen verschiedene Saetze: ist
                // der Bestand abgearbeitet, stimmt "nichts wartet"; ist das
                // Kontingent leer, gibt es die Produkte sehr wohl.
                isEmpty={current.product_count === 0 || (worstTargetCount === 0 && !journey.quotaBlocked)}
                quotaExhausted={worstTargetCount === 0 && journey.quotaBlocked}
                loading={feedCheckOptimize.loading}
                mcAccessRevoked={isMcAccessRevoked}
                optimizationRunning={optimization.isOptimizing}
                autoUpload={journey.autoUpload}
                // Nur im upload-Zweig ersetzt der Knopf die Navigation durch
                // die echte Handlung; approve/connect bleiben Navigation.
                onUploadRun={ctaStep === 'upload' ? upload.handleUploadButtonClick : undefined}
                uploadBusy={upload.uploadSubmitting || upload.isUploading}
                uploadElsewhere={foreignUploadRunning && upload.activeUploadJob
                  ? { uploaded: upload.activeUploadJob.uploaded_products, total: upload.activeUploadJob.total_products }
                  : undefined}
              />
            ) : statsFailed ? (
              <Card data-testid="cta-failed" style={STANDARD_STYLE} className="flex flex-col items-start gap-3 p-6 print:hidden">
                <p role="alert">{t('feedCheck.cta.countsFailed')}</p>
                <Button variant="outline" size="sm" onClick={() => refetchStats()}>
                  {t('feedCheck.cta.retry')}
                </Button>
              </Card>
            ) : (
              // Ehrlicher Ladezustand statt einer Zahl, die gleich kippt.
              <Skeleton data-testid="cta-pending" className="h-40 w-full rounded-2xl print:hidden" />
            )}
          </div>

          {/* Zeile 2: die EINE Liste mit Baustellen. Sie ersetzt die drei, die
              bis Paket 3 dieselbe Frage beantworteten (Top-3-Baustellen,
              Komponenten-Balken, technische Checks) -- dazu den Drift-Block,
              der als Zeile ihres zweiten Abschnitts weiterlebt. */}
          <FeedCheckCostList
            breakdown={buildBreakdown(current, compareSnapshot)}
            readiness={readinessEntries(validationSummary)}
            // Scheitert nur ein Refetch, haelt React Query die Liste: dann sagt
            // die Liste die Wahrheit und die Statuszeile bleibt weg (wie beim
            // Benchmark). Eine leere Liste ist definiert und kein Fehler.
            readinessFailed={validationFailed && validationSummary === undefined}
            onRetryReadiness={() => { void refetchValidation(); }}
            driftCount={driftCount}
            googleRejects={googleRejects}
            // F-A: die Baustellen-Aktion ist der Sekundaer-Einstieg in den
            // Optimierungslauf. Ohne `offersOptimizeSecondary` waere sie bei
            // offener Freigabe komplett weg, obwohl der Lauf dort weiterhin
            // moeglich ist -- nur eben nicht der naechste gemessene Schritt.
            // A2O-10: EIN Lauf statt sechs Komponenten-Versprechen, deshalb
            // `runCount` statt eines Schluessels je Komponente.
            onRunOptimize={(offersOptimize || offersOptimizeSecondary) ? () => runOptimize('issue') : undefined}
            runCount={worstTargetCount}
            // Ein laufender Lauf sperrt die Links genauso wie ein gerade
            // ausgeloester, und ein widerrufener MC-Zugriff genauso wie beide:
            // sonst legt ein Klick einen ZWEITEN Job ueber dieselben Produkte
            // an bzw. loest nur den Fehler-Toast aus useOptimizationJob aus.
            fixBusy={feedCheckOptimize.loading || optimization.isOptimizing || isMcAccessRevoked}
            // S5 des Final-Reviews: der Grund muss SICHTBAR sein, nicht nur als
            // title. Dieselbe Rangfolge wie in der Handlungskarte (Widerruf
            // gewinnt gegen laufenden Lauf); ein reines `loading` bekommt
            // bewusst keinen Satz, dafuer steht der Lauf im Fortschritts-Modal.
            blockedReason={
              isMcAccessRevoked
                ? t('feedCheck.cta.blockedMcRevoked')
                : optimization.isOptimizing
                  ? t('feedCheck.cta.blockedRunning')
                  : undefined
            }
            onFillUnitPricing={handleFillUnitPricing}
            fillBusy={fillBusy}
            onOpenWorkbench={(key) => { void openRuleInWorkbench(key); }}
            workbenchBusyKey={ruleBusyKey}
            // Ziel ist die Drift-Automatik in den Einstellungen, NICHT die
            // Werkbank: die laedt nur pending_review, drift-geflaggte Produkte
            // stehen per Konstruktion auf approved und erscheinen dort nie.
            onOpenDriftSettings={() => navigate('/settings?tab=schedules')}
          />

          {/* Zeile 3: Verteilung und Verlauf. */}
          {kapitel1Rest}

          {/* Zeile 4: die Kapitel 2 und 3, eingeklappt. Das Preis-Kapitel
              rendert die unveraenderte `PriceBenchmarkSection`, sobald es offen
              ist -- die Karte reicht sie als Knoten herein, damit die
              Kapitel-Komponente die Section nicht selbst importieren muss. */}
          <FeedCheckChapters
            feedId={selectedFeedId}
            benchmark={benchmarkSummary ?? null}
            benchmarkFailed={benchmarkFailed && !benchmarkSummary}
            onRetryBenchmark={() => { void refetchBenchmark(); }}
            priceChapterIndex={priceChapterIndex}
            chapterTotal={chapterTotal}
            priceSection={benchmarkSummary
              ? <PriceBenchmarkSection summary={benchmarkSummary} chapterTotal={chapterTotal as 2 | 3 | 4} />
              : undefined}
            liveRow={selectedFeedId ? (
              <LiveChapterRow
                chapterIndex={liveChapterIndex}
                chapterTotal={chapterTotal}
                feedId={selectedFeedId}
                live={live}
                onReview={() => navigate(WORKBENCH_TO_REVIEW_TARGET)}
                onUpload={upload.handleUploadButtonClick}
                uploadBusy={upload.uploadSubmitting || upload.isUploading || isMcAccessRevoked}
                disapprovedCount={googleRejects?.count ?? 0}
                openRequest={chapterRequest}
              />
            ) : undefined}
            adsRow={adsLoading ? (
              <AdsChapterRowSkeleton chapterIndex={adsChapterIndex} chapterTotal={chapterTotal} />
            ) : (
              <AdsChapterRow
                openRequest={chapterRequest}
                interest={adsInterest}
                chapterIndex={adsChapterIndex}
                chapterTotal={chapterTotal}
                feedId={selectedFeedId}
                feedName={feed?.display_name ?? null}
                feedLanguage={feed?.content_language ?? null}
                merchantCenterId={merchantCenter?.id ?? null}
                merchantCenterName={merchantCenter?.name ?? null}
                googleAccountId={selectedGoogleAccountId ?? null}
                state={adsState}
                linked={linkedAds ?? null}
                // A2-NEU-8: dieselbe Live-Zahl wie in der Kostenliste und im
                // Kapitel "Bei Google", statt der Cache-Zahl von gestern.
                parsed={adsSummary ? withLiveDisapproved(adsSummary, googleRejects?.count) : null}
                flow={adsFlow}
                // Derselbe Lauf, den die Handlungskarte und die Baustellen-
                // Knoepfe ausloesen: `runOptimize` waehlt serverseitig
                // `optimizationStatus: 'not_optimized'` und trifft damit die
                // Grundgesamtheit der Zeile "nie optimiert trotz Klicks" (eine
                // groessere Menge, aber die richtige). Ein Sprung in die
                // Werkbank waere nachweislich falsch: die laedt nur
                // `optimization_status = 'completed'`, also genau das
                // Gegenteil.
                onOptimizeUnoptimized={(offersOptimize || offersOptimizeSecondary) ? () => runOptimize('issue') : undefined}
                // Dieselbe Groesse, die die Baustellen-Knoepfe sperrt: die
                // Zeile ruft denselben Lauf, und ein zweiter Klick daneben
                // startete ihn ein zweites Mal.
                fixBusy={feedCheckOptimize.loading || optimization.isOptimizing || isMcAccessRevoked}
                // Fuer die abgelehnten Produkte gibt es noch kein Ziel: die
                // Werkbank kennt weder einen Ablehnungs-Filter noch die
                // Gruende (Session 3 des Sessionplans baut die Parameter).
                // Ohne Handler rendert `LeakRow` keinen Knopf, und kein Knopf
                // ist besser als einer, der ins Leere zeigt.
              />
            )}
          />

          {/* Review-Pipeline (Spec 2026-09-05, E14): die Bitte um eine
              Bewertung steht UNTER der Handlung, nie davor -- die Leiter
              behaelt die Vorfahrt. Rendert null, solange kein Wertmoment
              vorliegt oder der Nutzer schon geklickt/abgelehnt hat. */}
          <ReviewRequestCard />
        </>
      )}

      {/* Optimization progress + enforcement dialogs — same OptimizationProvider
          instance that the CTA's onOptimize call feeds into (see
          useOptimization()). Sie haengen IMMER im Baum: eine Blockade
          (Kontingent, Re-Optimierungs-Sperre) darf nicht davon abhaengen, ueber
          welche URL man die Seite betreten hat — sonst scheitert der Klick eines
          Bestandskunden stumm.
          Das Fortschritts-Modal traegt zusaetzlich das Herkunfts-Gate
          (`startedHereRef`): nur ein hier gestarteter Lauf zieht es auf.
          Der Erfolgszustand kennt seit Task 11 nur noch EINEN Weg: in die
          Werkbank. Der frueher gleichrangige "Direkt zu Google"-Knopf ist
          ersatzlos weg, nicht nur auf dieser Seite ausgeblendet — die
          Pflicht-Freigabe will genau keinen zweiten Weg an ihr vorbei. */}
      <OptimizationProgressModal
        feedId={selectedFeedId}
        open={optimization.showProgressModal && startedHereRef.current}
        onOpenChange={optimization.setShowProgressModal}
        onReviewWorkbench={(ctx?: OptimizationScoreDelta) => {
          // C-19: der Kontext reist als Router-State mit, nicht in der URL —
          // er ist bewusst fluechtig (ein Reload der Werkbank verliert ihn
          // wieder und zeigt die statische Subline). Genau das soll er: eine
          // Momentum-Zeile fuer GENAU diesen Uebergang, keine dauerhafte
          // Behauptung "du bist gerade fertig geworden". Nur mitgeben, wenn
          // wenigstens ein gemessener Wert vorliegt — ein leeres `{pre:null,
          // post:null}` waere kein Kontext, nur zusaetzliches State-Rauschen.
          //
          // Review-Fix Runde 1 (Feed-Scope-Leak): `selectedFeedId` wird HIER,
          // an der Navigations-Stelle, frisch gelesen und in den State
          // gemischt — nicht vom Modal selbst gesetzt. Der globale
          // `FeedSwitcher` (Layout) kann den Feed wechseln OHNE zu
          // navigieren; die Werkbank muss den mitgereisten Score-Sprung daher
          // gegen den zum Ansichtszeitpunkt AKTUELLEN `selectedFeedId`
          // pruefen koennen (siehe Workbench.tsx). Ohne Feed-ID im State
          // waere das nicht moeglich, und der Score-Sprung von Feed A liefe
          // Gefahr, faelschlich Feed B zugeschrieben zu werden.
          // Ziel mit Filter (Spec 2026-09-16, Abschnitt 7): der Lauf hat
          // gerade Produkte auf `pending_review` geschrieben, und die Werkbank
          // soll GENAU DIESE Menge zeigen -- nicht den rohen Reiter-Vorrat, in
          // dem laengst hochgeladene Zeilen mitliegen (auf googleshopping.xml
          // 3.830 von 3.961, gemessen 31.07.2026).
          const hasMeasuredValue = ctx && (ctx.pre != null || ctx.post != null);
          if (hasMeasuredValue && selectedFeedId) {
            const successCtx: OptimizationSuccessContext = { ...ctx, feedId: selectedFeedId };
            navigate(WORKBENCH_TO_REVIEW_TARGET, { state: { optimizationSuccess: successCtx } });
          } else {
            navigate(WORKBENCH_TO_REVIEW_TARGET);
          }
        }}
      />
      <UpgradeModal open={optimization.showUpgradeModal} onClose={() => optimization.setShowUpgradeModal(false)} />
      <LargeRunConfirmDialog
        open={pendingLargeRun !== null}
        count={worstTargetCount}
        remainingAfter={quotaLoading ? null : Math.max(0, remainingCapacity - worstTargetCount)}
        onCancel={() => setPendingLargeRun(null)}
        onConfirm={() => {
          const source = pendingLargeRun;
          setPendingLargeRun(null);
          if (source) void startRun(source);
        }}
      />
      <ReoptimizeConfirmDialog
        open={optimization.showReoptimizeDialog}
        onClose={() => optimization.setShowReoptimizeDialog(false)}
        onConfirm={optimization.confirmReoptimize}
        droppedNew={optimization.reoptimizeDroppedNew}
      />
      <QuotaLimitDialog
        open={optimization.showQuotaCapDialog}
        info={optimization.quotaCapInfo}
        onClose={() => optimization.setShowQuotaCapDialog(false)}
        onProceed={optimization.confirmQuotaCapOptimize}
      />

      {/* Upload-Dialoge (Bestaetigen/Fortschritt/Erfolg), F-B Teil 2 — geteilt
          mit der Werkbank (`WorkbenchUploadAction`) ueber `UploadFlowDialogs`,
          damit Race-Verhalten wie das Herkunfts-Gate des Fortschrittsdialogs
          oder die N7-Zaehlregel nur an EINER Stelle gepflegt werden. Haengt
          bewusst NICHT an `ctaStep === 'upload'`: sobald der Upload durch ist,
          faellt die wartende Menge auf null, der Fortschritts- und Erfolgs-
          dialog wuerden sonst mitten im Lauf verschwinden (Muster
          `WorkbenchUploadAction`). `beforeUpload` entfaellt hier -- diese
          Seite hat keine Inline-Edits, die vor dem Upload weggeschrieben
          werden muessten. `optimization.isOptimizing` sperrt den Upload-Knopf
          oben zusaetzlich (der `OptimizationProvider` ist hier gemountet) --
          eine Sperre, die `WorkbenchUploadAction` bewusst NICHT hat (kein
          Provider auf `/workbench`, siehe dortiger Kommentar); hier schliesst
          sie die Luecke fuer DIESE Seite. */}
      {selectedFeedId && (
        <UploadFlowDialogs
          feedId={selectedFeedId}
          upload={upload}
          approvedCount={stats.approved}
          analyticsSource="feed_check"
        />
      )}

      {/* Sync-Dialoge, aus dem aufgeloesten SimpleHome uebernommen. Sie gehoeren
          dorthin, wo der Ladeknopf steht, und der steht seit Task 13 hier. */}
      <FetchProgressDialog
        open={fetch_.showFetchDialog}
        productCount={fetch_.fetchProgress.count}
        status={fetch_.fetchProgress.status}
        page={fetch_.fetchProgress.page}
        syncStats={fetch_.fetchProgress.syncStats}
        onClose={() => { fetch_.setShowFetchDialog(false); safetyHint.showIfFirstTime(); }}
        /* Uebergabe nach dem Sync (Spec 7): der naechste Schritt ist das
           Optimieren der frisch geholten Produkte. Die Zahl kommt aus dem
           gerade gemessenen Angebot (`worstTargetCount`), das Ziel aus der
           Leiter.
           Drei Faelle, und alle drei brauchen ihr eigenes Verhalten, sonst
           widersprechen Beschriftung und Ziel einander:
           - Angebot vorhanden und Kontingent frei: Dialog schliessen und den
             Lauf starten.
           - Angebot 0 bei freiem Kontingent (der Normalfall eines fertig
             optimierten Feeds): `NextStepCta` beschriftet sich als Weg zum
             Feed-Check, und der Feed-Check IST diese Seite -- also nur den
             Dialog schliessen (Plan-Entscheidung E7). Ohne dieses `onAction`
             navigierte der Knopf auf `targets.optimize`, also in die Werkbank.
           - Kontingent erschoepft: kein `onAction`, das Ziel aus der Leiter
             ist `/pricing` und die Beschriftung sagt das auch. */
        handoff={selectedFeedId ? (
          <NextStepCta
            step="optimize"
            from="connect"
            count={worstTargetCount}
            className="w-full"
            onAction={journey.quotaBlocked
              ? undefined
              : worstTargetCount > 0
                ? () => { fetch_.setShowFetchDialog(false); runOptimize('cta'); }
                : () => fetch_.setShowFetchDialog(false)}
          />
        ) : undefined}
        onCancel={fetch_.cancelFetch}
        cancelling={false}
        isStale={fetch_.isStale}
        feedProductCount={fetch_.feedProductCount}
        estimatedProductCount={feed?.estimated_product_count}
        recentTitles={fetch_.fetchProgress.recentTitles}
      />
      <SafetyHintDialog open={safetyHint.open} onClose={() => safetyHint.setOpen(false)} />

      <GoogleAccountWizard
        mode="onboarding"
        open={showWizard}
        onClose={() => setShowWizard(false)}
        onSuccess={async (googleAccountId) => {
          setShowWizard(false);
          if (googleAccountId) {
            await setSelectedGoogleAccount(googleAccountId);
          }
          queryClient.invalidateQueries();
          refreshSelection();
          if (getFlowIntent() === 'feedcheck') {
            clearFlowIntent();
            // Der Report liegt seit Task 13 auf `/`, also auf genau dieser
            // Seite. Der Parameter bleibt als Trichter-Marke stehen.
            navigate('/?feedcheck=1');
          }
        }}
        onOptimizeAll={(productCount) => {
          // Bericht 8.3 Punkt 8: hier wird bewusst NICHT mehr geclearrt. Das
          // pauschale clearFlowIntent() stammt aus dem ersten Funnel-Plumbing
          // (Commit 7f937ea, ohne dokumentierten Grund) und diente demselben
          // Zweck wie die spaeter ergaenzte 24h-TTL in lib/flowIntent.ts:
          // verhindern, dass spaetere manuelle Syncs dauerhaft als
          // Feed-Check-Syncs geflaggt werden. Es kostete aber den
          // versprochenen Report: wer im Assistenten direkt "Optimieren"
          // klickt, durchlaeuft onSuccess nie (ReadyStep ruft nur
          // handleClose + onOptimizeAll) — der Marker war weg, bevor die
          // notify_feed_check-Leser (useFetchProducts, DownloadProductsStep)
          // oder der Report-View-Effekt ihn je verbrauchen konnten. Der
          // Marker ueberlebt den Klick jetzt; verbraucht wird er weiterhin
          // vom Report-View-Effekt oben (nach angezeigtem Report) bzw. von
          // der TTL in getFlowIntent().
          startedHereRef.current = true;
          optimization.handleOptimize({
            optimizationType: 'both,attributes',
            isAdvanced: true,
            selectAllMode: { enabled: true, estimatedCount: productCount },
            advancedSelectedIds: new Set<string>(),
            advancedFilters: { optimizationStatus: 'not_optimized' },
            hasUnoptimizedProducts: true,
            totalProductCount: productCount,
            // Zweiter rein-neuer Einstieg neben dem Feed-Check: gefiltert wird
            // auf `not_optimized`, der Assistent laeuft auf einem gerade erst
            // verbundenen Feed. Ohne diese Zahl schweigt die
            // Kapazitaets-Vorwarnung in `handleOptimize` genau hier — ein
            // Gratis-Konto mit 5.000er-Feed bekaeme den Hinweis erst als roten
            // Toast NACH dem Start, statt vorher als Dialog mit Wahl.
            //
            // `productCount` ist die gerade gesynkte Feed-Groesse. Auf dem
            // Erstkontakt-Pfad ist das genau die Neu-Menge; auf einem bereits
            // teil-optimierten Feed kann sie zu hoch liegen und die Vorwarnung
            // faellt eine Spur zu frueh. Das ist die sichere Richtung — der
            // Dialog bietet "trotzdem starten" an, das Gegenteil waere ein
            // stiller Verbrauch der letzten Plaetze.
            newProductCount: productCount,
          });
        }}
      />
    </div>
  );
};

const FeedHealthDashboard: React.FC = () => {
  const { selectedFeedId, feed } = useSelection();
  const { user } = useAuth();
  // Stripe-Rueckkehr landet auf `/?success=true`. Der Haken sass in SimpleHome
  // und muss mit umziehen, sonst faellt die Kaufmessung stumm aus.
  useCheckoutSuccess();

  // Onboarding-Assistent. Bedingung unveraendert aus SimpleHome uebernommen:
  // er oeffnet genau dann, wenn der Nutzer null Merchant Center hat.
  const [showWizard, setShowWizard] = useState(false);
  const [wizardChecked, setWizardChecked] = useState(false);

  // Feed-Check-Landung: `?flow=feedcheck` persistieren, bevor der Assistent startet.
  useEffect(() => {
    captureFlowIntent();
  }, []);

  useEffect(() => {
    if (!user || wizardChecked) return;
    const checkNewUser = async () => {
      const { count } = await supabase
        .from('merchant_centers')
        .select('*', { count: 'exact', head: true })
        .limit(1);
      if ((count || 0) === 0) setShowWizard(true);
      setWizardChecked(true);
    };
    checkNewUser();
  }, [user, wizardChecked]);

  return (
    <OptimizationProvider feedId={selectedFeedId} feedProductCount={feed?.estimated_product_count ?? 0}>
      <FeedHealthDashboardInner showWizard={showWizard} setShowWizard={setShowWizard} />
    </OptimizationProvider>
  );
};

export default FeedHealthDashboard;
